/* eslint-disable max-lines */
import { AGENT_HOME_V1_FLAG_KEY } from './agent-home';
import {
  DESKTOP_DICTATION_FLAG,
  DISABLE_PUSH_TO_EPIC_FLAG_KEY,
  IS_PLATFORM_DICTATION_ENABLED,
  ONBOARDING_FLOW_FLAG_KEY,
  REGIONAL_UPDATER_FLAG_KEY,
  WEB_DICTATION_FLAG,
  type RegionalUpdaterGate,
} from './legacy-flags';

import {
  LABS_FEATURE,
  useIsAutoEnrolledSessionLayoutV2,
  useLabsFeatureOptIn,
} from '@/app-shell/settings/heidi-labs/early-access/labs-overrides';
import { useIsPlatformDictationEnabled } from '@/composition/hosts/dictation/hooks/useIsPlatformDictationEnabled';
import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import {
  HEIDI_2_OPT_IN_FLAG_KEY,
  useIsAgentSessionRecordingEnabled,
  useIsProjectsContextualLoaderPhrasesEnabled,
  useIsProjectsEnabled,
  useIsSessionRoutineRunsEnabled,
  useIsSidebarIAEnabled,
  useIsWorkflowsEnabled,
  useIsWorkLetterArtifactEnabled,
} from '@/features/flags/heidi-work-umbrella';
import { useCachedFlagFallback } from '@/features/flags/offline-cached-flag-decisions';
import {
  OFFLINE_RECORDING_FLAG_KEY,
  useCachedOfflineRecordingFlagFallback,
} from '@/features/flags/offline-recording-flag-cache';
import {
  useFeatureFlagEnabled,
  useFeatureFlagVariantKey,
} from '@/features/flags/posthog-compat';
import {
  PROBLEM_BASED_CHARTING_FLAG_DEFAULT,
  PROBLEM_BASED_CHARTING_FLAG_KEY,
} from '@/features/flags/problem-based-charting-flag';
import { SESSION_PAGE_V2_FLAG_KEY } from '@/features/flags/session-page-v2-flag';
import { WAV_WRITER_RECOVERY_FLAG_KEY } from '@/features/flags/wav-writer-recovery-payload';
import { useManualExposure } from '@/hooks/amplitude';
import { CERNER_EMBEDDED_BROWSER_DIAGNOSTICS_FLAG } from '@/integrations/sof-launch/cerner-embedded-diagnostics';
import { RtlLocaleFlagContext } from '@/providers/os-locale-context.utils';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { useContext, useEffect, useState } from 'react';
// IMPORTED, not just re-exported. SIX names across four domains are bound
// locally here, and every one of them for the same reason: a getter that STAYS
// in this barrel composes it. A `export … from` line publishes a name without
// putting it in scope, so the composition would not compile.
//
// The voice-assistant gate is composed by `useIsVoiceDictationModeEnabled`,
// which ANDs a PostHog-resolved flag this door carries no surface for. The
// Projects umbrella is composed in `./heidi-work-umbrella`. `./flags` is a
// headless mechanism door, so this costs the barrel's tauri-boot importers
// nothing.
import { useIsVoiceAssistantEnabled } from '@heidi/agents/flags';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
// Composed by app-side platform gates below — see the note above.
// `useIsDictateDeviceSetupVisible`, `useIsDictateHardwareMicEnabled` and
// the user-feedback gate below compose these.
import {
  useIsDictateDeviceDetectedPopupEnabled,
  useIsDictateDeviceSetupEnabled,
  useIsDictateUserFeedbackEnabled as useIsDictateUserFeedbackFlagEnabled,
  useIsDictationImportSnippetsEnabled as useIsDictationImportSnippetsFlagEnabled,
} from '@heidi/dictate/flags';
import {
  getFlagGate,
  resolveFlagGate,
  useBooleanFlagLite,
} from '@heidi/runtime/feature-flags';
// Composed by `useIsRibbonSidebarEnabled` and by the two Session Page V2 gates,
// all three of which stay here — see the note above.
import {
  NEW_SESSION_LIST_FLAG_KEY,
  useIsNewSessionListEnabled as useIsNewSessionListFlagEnabled,
  useIsStableSessionSplitStartupEnabled,
} from '@heidi/session/flags';
// Both getters moved to `@heidi/workspace/flags` in batch 2, and
// `useIsWorkspaceSettingsIaEnabled` below — which stays here, because it
// declares no key and its body composes rather than reads — ANDs them.
import type { MultiWorkspaceSelectionGate } from '@heidi/workspace/flags';
import {
  useIsMultiWorkspaceMembershipEnabled,
  useIsWorkspaceSettingsEnabled,
  useIsWorkspaceSwitcherEnabled,
} from '@heidi/workspace/flags';
import {
  OpenFeature,
  ProviderEvents,
  ProviderStatus,
  useBooleanFlagValue,
  useOpenFeatureClientStatus,
} from '@openfeature/react-sdk';
import posthog from 'posthog-js';

/**
 * Centralized English-market gating (browser-language based, works pre- and
 * post-auth). `isExcludedByEnglishOnly` takes a per-experiment `op-…-english-only`
 * flag key. Re-exported here so flag consumers can import them alongside the
 * experiment hooks; the source lives in `./market` to avoid a circular import
 * with `./legacy-flags`.
 */
export {
  isEnglishMarket,
  isExcludedByEnglishOnly,
  useIsExcludedByEnglishOnly,
} from './market';
/**
 * SINGLE CANONICAL SURFACE (P3 · monorepo-wave-1-p3-feature-flags · U1/Z2).
 * `@/features/flags` is the one public import path for feature flags: the
 * legacy `IS_*` surface (now `./legacy-flags`, relocated at Z2 from
 * `src/components/constants/flags.ts`) re-exports through this barrel.
 * Consumers never import the leaf directly; tests that stub individual
 * `IS_*` declarations `jest.mock('@/features/flags/legacy-flags')` — the
 * star re-export carries a leaf mock through to barrel consumers.
 *
 * Star-overlap audit (2026-07-18): the two files share NO export names
 * (single-line, brace-form, and default exports all checked), so `export *`
 * carries the full legacy surface with no shadowing and no TS2308 ambiguity.
 * The four legacy names imported at the top of this file are imports, not
 * re-declarations — the star re-export is their only path through the barrel.
 */
export {
  useIsEventTriggersEnabled,
  useIsSessionTranscribingEventsEnabled,
  useIsWorkflowEventRuleConditionsEnabled,
} from '@heidi/agents/flags';
export {
  CAPABILITY_SIGNALLING_DEFAULT,
  CAPABILITY_SIGNALLING_FLAG_KEY,
  CAPABILITY_SIGNALLING_VARIANTS,
  parseCapabilitySignallingVariant,
  useCapabilitySignallingVariant,
  type CapabilitySignallingVariant,
} from './capability-signalling-flag';
export { useIsChenmedEngineV2Enabled } from './chenmed-engine-v2-flag';
export {
  DIARISATION_UI_FLAG_DEFAULT,
  DIARISATION_UI_FLAG_KEY,
  useIsDiarisationUiEnabled,
} from './diarisation-ui-flag';
export {
  DICTATE_REENGAGEMENT_FLAG_KEY,
  DICTATE_REENGAGEMENT_HINT_VERSIONS,
  DICTATE_REENGAGEMENT_VIEW_EXPERIMENT_KEY,
  useDictateReengagementFlag,
  useIsDictateReengagementEnabled,
  type DictateReengagementHintVersion,
} from './dictate-reengagement-flag';
export * from './legacy-flags';
export { useIsOfrepDummyFlagEnabled } from './ofrep-dummy-flag';
export {
  PATIENT_DIGITAL_TWIN_FLAG_KEY,
  useIsPatientDigitalTwinEnabled,
} from './patient-digital-twin-flag';
export { useIsSafetyIssueCheckboxEnabled } from './safety-issue-checkbox-flag';
export { useIsSourceControlNestedSourcesEnabled } from './source-control-flags';
// The boolean translator getters are declared and read once in
// `packages/integrations/src/flags.ts` (ruling 36); the two that stay app-side
// compose STRING flags no descriptor can hold a default for yet.
export {
  useTranslatorEchoCancellationToggleEnabled,
  useTranslatorEvaluationUiEnabled,
  useTranslatorMonashEvaluationEnabled,
  useTranslatorNavEnabled,
  useTranslatorNuhSessionFeedbackEnabled,
  useTranslatorScribeEnabled,
  useTranslatorSessionEvaluationSurface,
} from '@heidi/integrations/flags';
export {
  useTranslatorLanguageGates,
  useTranslatorOnboardingVariant,
} from './translator-flags';

// NOT re-exported through this barrel: `useIsUnifiedCollectionsEnabled` pulls in
// the Kinde-properties query chain (store/auth), which loops back into this
// still-initializing module and trips a TDZ error on a later `const`
// (`AMBIENT_COPILOT_FLAG_KEY`), failing ~19 unit suites on shard 3 (EVI-2333).
// Import it directly from './unified-collections-flag' at the call site — the
// same reason `./market` is split out (see the header note above).

export const useIsEricTestFlagEnabled = () => {
  return useBooleanFlagValue('eric-test-flag', false);
};

/**
 * Cerner MPage embed mic/context troubleshooting (WHTH). Logs sanitized browser
 * capability snapshots only — no tokens or patient data. PostHog flag.
 */
export const useIsCernerEmbeddedBrowserDiagnosticsEnabled = () => {
  return useFeatureFlagEnabled(CERNER_EMBEDDED_BROWSER_DIAGNOSTICS_FLAG) ?? false;
};

/**
 * Flagsmith: gates the selection → Ask Heidi actions — AI actions (Rewrite /
 * Make shorter / Check guidelines / Ask anything) on the vMarkdown text
 * selection bubble menu, plus the "Selected text" badge on the floating Ask
 * Heidi bar. Default OFF.
 */
export const useIsSelectionAskHeidiEnabled = () => {
  return useBooleanFlagValue('release-scribe-selection-ask-heidi', false);
};

/**
 * Gates rendering of the template-compliance "Fix for me" action. The
 * per-flow compliance flags still control eligibility after this rollout gate.
 * Default OFF.
 *
 * MOVED to `@heidi/templates` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/templates/src/flags.ts`.
 */
export { useIsTemplateComplianceFixForMeEnabled } from '@heidi/templates/flags';

/**
 * Desktop-only rollout gate for template-scoped SystmOne flow authoring.
 * The templates domain owns the descriptor and getter.
 */
export { useIsRpaFlowEnabled } from '@heidi/templates/flags';

/**
 * Magic Flow onboarding entry points on web and desktop. A separate flag from
 * `useIsRpaFlowEnabled`, so onboarding and the desktop authoring pilot roll out
 * independently. The templates domain owns the descriptor and getter.
 */
export { useIsMagicFlowOnboardingFlagEnabled } from '@heidi/templates/flags';

/**
 * Gates in-note medication detection highlights and Evidence quick actions.
 * Flagsmith/OpenFeature, default OFF.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`, hook and imperative reader off ONE
 * descriptor.
 *
 * THE KEY MOVED WITH THEM AND IS STILL EXPORTED HERE. It is a barrel export with
 * a live reader, so it cannot vanish; re-declaring the literal on this line
 * instead would be a SECOND MINTING SITE, which is the one thing ruling 36
 * exists to refuse. Neither side's own suite can see it: the package's
 * mint-count case reads only the package source, and this barrel's suite asserts
 * a VALUE, which a copy satisfies. What refuses it is
 * `scripts/check-flag-getter-doors.test.ts` ('a declared key is minted ONCE, on
 * both sides of the door'). So the package derives it from the descriptor —
 * `export const MEDICATION_HIGHLIGHTS_FLAG_KEY = medicationHighlightsFlag.key` —
 * and this line re-exports it beside its two getters.
 */
export {
  getIsMedicationHighlightsEnabled,
  MEDICATION_HIGHLIGHTS_FLAG_KEY,
  useIsMedicationHighlightsEnabled,
} from '@heidi/evidence/flags';

/**
 * Kill switch for modal pacing (quiet period / recording suppress / per-session
 * cap on non-essential launch surfaces). PostHog, default OFF. Fed
 * into `isModalPacingEnabledAtom` by `ModalPacingSync`.
 */
export const useIsModalPacingEnabled = () => {
  return useFeatureFlagEnabled('release-growth-modal-pacing') ?? false;
};

/**
 * Gates the pre-action "you'll be signed out of all devices" warning shown
 * before sensitive account changes (currently: enabling MFA). The logout
 * itself is backend-driven session revocation (ml-scribe) — this flag must
 * stay in lock-step with the backend flag: warning without revocation would
 * mislead users, and suppressing the post-enable MFA setup modal without
 * revocation would strand them. Flagsmith/OpenFeature, default OFF.
 */
export const useIsLogoutEverywhereOnSensitiveActionsEnabled = () => {
  return useBooleanFlagValue(
    'release-ml-scribe-logout-everywhere-on-sensitive-actions',
    false
  );
};

/**
 * Gates the Account settings "Login with Heidi App" setup row.
 *
 * MOVED to `@heidi/workspace` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/workspace/src/flags.ts`. The row's `!isTauri()` guard is the
 * CONSUMER's (`login-with-heidi-app.tsx`), not this getter's, which is why the
 * body was a plain read and could move.
 */
export { useIsLoginWithHeidiAppEnabled } from '@heidi/workspace/flags';

/**
 * Gates the Health NZ enterprise-migration prompt. The flag is the whole of the
 * targeting — segmented onto solo accounts on a Health NZ email. Rollout gate,
 * not an experiment: no `useManualExposure`.
 */
export const useIsHealthNzMigrationPromptEnabled = () => {
  return useBooleanFlagValue('release-scribe-health-nz-migration-prompt', false);
};

/**
 * The two pinned compliance recommendations in the notifications popover —
 * "Set up MFA" and the inactivity sign-out nudge.
 *
 * PostHog, matching Growth's other new rollouts (`useIsDocxFormEnabled`). They
 * used to be Flagsmith reads on `@heidi/growth/flags`; that door has no PostHog
 * surface (ruling 36), so converting in-place would change which provider
 * answers. The getters live in the leaf below; the package takes the resolved
 * booleans as `flagGates`.
 */
export {
  useComplianceNotificationFlagGates,
  useIsMeddeviceInactivityLogoutNotificationEnabled,
  useIsMeddeviceMfaSetupNotificationEnabled,
} from './meddevice-compliance-notification-flags';

/**
 * Backend-translated specialty display names: `useQuerySpecialties` sources
 * each item's `localizedName` from the API's `localized_name` instead of the
 * FE i18next `specialties.*` catalog.
 */
export const useIsSpecialtyBackendTranslationEnabled = () =>
  useBooleanFlagValue('release-scribe-be-specialty-translation', false);

/**
 * Kill-switch for the "Scribe changed to …" success confirmation toast shown
 * after a scribe switch triggers a note/document regeneration (V1
 * customise-template menu + V2 note-context-card change-scribe menu).
 * Defaults ON so the confirmation ships enabled; flip OFF in Flagsmith to
 * suppress the toast without a deploy if it regresses.
 */
export const useIsScribeChangeConfirmationEnabled = () => {
  return useBooleanFlagValue('release-scribe-change-confirmation', true);
};

/** Declared in `@heidi/session/flags`; re-exported for app importers. */
export { useIsCreateSessionOnEmptyEnabled } from '@heidi/session/flags';

/** Follows the Heidi Work umbrella; declared in `./heidi-work-umbrella`. */
export { useIsV2HeidiSuggestsEnabled } from '@/features/flags/heidi-work-umbrella';

/**
 * Gates the AN-ACC reassessment evidence-prep PoC surfaces (Heidi Verify AN-ACC
 * group + the editable AN-ACC class field). Declared and read once in
 * `packages/patients/src/flags.ts` (ruling 36), so the key is minted there.
 */
export { useIsAnaccVerifyEnabled } from '@heidi/patients/flags';

export const useIsVerifyLiteEnabled = () => {
  return useBooleanFlagValue('release-scribe-verify-lite', false);
};

export const useExpandedSourceControlTrackingEnabled = () => {
  return (
    useFeatureFlagEnabled('release-growth-enable-extra-source-control-tracking') ?? false
  );
};

/**
 * Clinical web drill-in panel inside source control V2 (guidelines toggle +
 * inline country / jurisdiction / specialty edits). Rollout/kill gate layered
 * Independent rollout gate — source control V2 stays ON while this panel stays
 * OFF (e.g. EU/UK PostHog cohort exclusion, gradual ramp). Default OFF.
 */
export const useIsSourceControlClinicalWebPanelEnabled = () => {
  return useFeatureFlagEnabled('release-source-control-clinical-web-panel') ?? false;
};

/**
 * Gates expanded analytics tracking for the in-session "Create" button —
 * a single `button_clicked` event with the resolved `creation_path`
 * (auto-mode vs default-template generation vs opening the template or
 * context modal). Defaults OFF so the Create-button tracking can be
 * rolled out independently from the sources popover and template
 * picker tracking flags.
 */
export const useExpandedCreateButtonTrackingEnabled = () => {
  return (
    useFeatureFlagEnabled('release-growth-enable-extra-create-button-tracking') ?? false
  );
};

export const useExpandedTemplatePickerTrackingEnabled = () => {
  return (
    useFeatureFlagEnabled('release-growth-enable-extra-template-picker-tracking') ?? false
  );
};

/**
 * MUL-3181: the workspace-settings shared-patient-directory opt-in section.
 * The SAME Flagsmith flag the ml-scribe backend gates the consent write on.
 * Declared and read once in `packages/workspace/src/flags.ts`.
 */
export { useIsSharedPatientDirectoryEnabled } from '@heidi/workspace/flags';

/**
 * Multi-workspace membership: the post-login workspace selector for
 * users in 2+ workspaces. Declared and read once in
 * `packages/workspace/src/flags.ts`. Evaluated at the hydration gate, BEFORE
 * the user identity reaches the provider, so it rolls out per environment.
 * Published from a local binding because `useMultiWorkspaceSelectionGate`
 * below composes it with `useIsFlagProviderSettled`; act on THAT, never on
 * this raw read.
 */
export { useIsMultiWorkspaceMembershipEnabled };

/**
 * EVI-1125: org-level Evidence settings (clinical sources + clinical web) for
 * enterprise admins.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsOrgEvidenceSettingsEnabled } from '@heidi/evidence/flags';

/**
 * EVI-2318: International option on Evidence settings clinical-profile country.
 *
 * Declared in `@heidi/evidence/flags`. Medical-device-regulated countries
 * (EU/UK including SE_AZ1) stay off via Flagsmith targeting plus a fail-closed
 * region deny-list at the settings surface.
 */
export { useIsInternationalEvidenceCountryEnabled } from '@heidi/evidence/flags';

/**
 * Kill switch (default ON) for the muted Key Safety Notes styling. Declared in
 * `@heidi/evidence/flags`; callers AND it with the Kinde region through
 * `shouldMuteKeySafetyNotes`, so UK/EU/SE_AZ1 and unresolved regions keep the
 * certified block whatever the flag says.
 */
export { useIsMutedKeySafetyNotesEnabled } from '@heidi/evidence/flags';

/**
 * EVI-2425: team-level Evidence settings page + client enforcement. Migrated
 * from PostHog `IS_TEAM_EVIDENCE_SETTINGS_ENABLED` onto Flagsmith, same key.
 *
 * Declared and read once in `packages/evidence/src/flags.ts`.
 */
export { useIsTeamEvidenceSettingsEnabled } from '@heidi/evidence/flags';

/**
 * MUL-3411: kill switch for the org-level custom consent message textarea.
 * Ships ON — defaults to `true` so the field is shown by default and can be
 * pulled remotely via Flagsmith if the rollout needs to be stopped. Matches the
 * backend `release-custom-consent-text` flag. Only gates the org textarea; the
 * require-patient-consent toggle itself is already live and stays ungated.
 */
export const useIsOrgCustomConsentTextEnabled = () => {
  return useBooleanFlagValue('release-custom-consent-text', true);
};

/**
 * Flagsmith: gates the experimental design-system *forms* styles (the structural
 * experimental form-field / control recipes). Default OFF.
 *
 * Sibling to `useIsExperimentalDesignSystemButtonsEnabled` (the Button's
 * remaining success-tone colour rework), which only takes visual effect when this
 * forms flag is also on.
 */
export const useIsExperimentalDesignSystemFormsEnabled = () =>
  useBooleanFlagValue('release-experimental-design-system-forms', false);

/**
 * Flagsmith (DS-1202): gates the reworked `semantic-*` / `button-background-*`
 * colours for the Button's *success* tone (`success`, `success_ghost`, `codes`).
 * The destructive tone ships on the forms flag itself (DS-1567). Separate from the
 * `release-experimental-design-system-forms` flag that gates the button's
 * structural experimental styles — the colour rework only takes effect inside the
 * forms-experimental button. Default OFF.
 */
export const useIsExperimentalDesignSystemButtonsEnabled = () =>
  useBooleanFlagValue('release-experimental-design-system-buttons', false);

/**
 * PostHog: gates the sky-blue tier-color rollout. When on,
 * `TierColorThemeManager` adds the `skiesTier` / `skiesTierDark` override class
 * to `<html>`, recoloring token-driven `brand-*` / `tier-*` surfaces to one
 * sky-blue (hardcoded-hex glyphs/gradients need a separate sweep). App-root
 * theming toggle, not a DS-context recipe swap. Default OFF.
 */
export const useIsSkiesTierColorEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-skies-tier-color') ?? false;

export { useIsBrandedIconLibraryEnabled } from './branded-icon-library-flag';

export { useIsFullscriptCopyOpenEnabled } from '@heidi/integrations/flags';

export { useIsBodyFontRefreshEnabled } from './body-font-refresh-flag';

// Non-hook variant for dictation context resolvers used outside React.
export const isDictationContextEnabled = () =>
  IS_PLATFORM_DICTATION_ENABLED() &&
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-enable-dictation-context',
    false
  );

/**
 * Flagsmith (APP-5958): gates the install-source / MDM analytics enrichment
 * on desktop. When on, the Rust side persists the installer type (MSI vs NSIS)
 * at first launch and the frontend enriches every analytics event with it.
 */
export const useIsInstallSourceTrackingEnabled = () => {
  return useBooleanFlagValue('release-apps-install-source-tracking-desktop', false);
};

export const RTL_FLAG_KEY = 'release-scribe-enable-rtl';

/**
 * The ONE reactive read of the RTL flag. `HeidiIntlProvider` calls this and
 * publishes the value through `RtlLocaleFlagContext`; everything below reads
 * the context via `useIsScribeEnableRtlEnabled`.
 */
export const useScribeEnableRtlFlagSource = () =>
  useBooleanFlagValue(RTL_FLAG_KEY, false);

/**
 * Flagsmith: Arabic locale, translations, and RTL layout.
 *
 * Reads the value `HeidiIntlProvider` hoisted into `RtlLocaleFlagContext` (see
 * `os-locale-context.utils`): this getter sits inside `useTranslations()` and
 * `useQueryUserLanguage()`, so a per-instance SDK read made it ~40% of all flag
 * reads on the session page. Outside that provider (pre-auth shells, tests) it
 * is a synchronous client read — the current value on every render, with no
 * subscription of its own. Not a hook call in either branch, so the branch may
 * change between renders.
 */
export const useIsScribeEnableRtlEnabled = () => {
  const hoisted = useContext(RtlLocaleFlagContext);
  if (hoisted !== undefined) return hoisted;
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(RTL_FLAG_KEY, false);
};

/**
 * Flagsmith: master gate for transcript deletion (the delete-transcript UI and
 * any other FE surfaces). Definition only — the actual gating lives in the
 * consuming tickets. Default OFF; flip per-environment in Flagsmith.
 */
export const useIsTranscriptDeletionEnabled = () => {
  return useBooleanFlagValue('release-scribe-transcript-deletion', false);
};

/**
 * PostHog: gates the transcript-tab PDF download icon (start/end,
 * timestamps, and transcript text via POST /export-pdf) on the V1 and V2
 * live transcript gutters. Default OFF. The legacy plain-text pane is
 * out of scope — it has no chunk timestamps to format.
 * Cloud reads PostHog; on-prem `useFeatureFlagEnabled` falls through to
 * OpenFeature and stays off until the key is in the on-prem bundle.
 */
export const useIsTranscriptPdfDownloadEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-transcript-pdf-download') ?? false;
};

/**
 * Flagsmith: MUL-1735 — org-admin bulk + per-row "log out everywhere" on the
 * organisation members table. Off by default so the destructive action can be
 * rolled out gradually and killed instantly. Gates the selection UI and the
 * per-row logout item (on top of the existing admin check).
 *
 * MOVED to `@heidi/workspace` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/workspace/src/flags.ts`.
 */
export { useIsOrgAdminBulkLogoutEnabled } from '@heidi/workspace/flags';

/** Flagsmith: post-signup prompt for pending team invites. */
export { useIsPendingTeamInvitePromptEnabled } from '@heidi/workspace/flags';

/**
 * Flagsmith: APP-3779 — desktop write-side cutover to the v2 EKS transcription
 * service for session recording (register / chunk upload / finish). Independent
 * of the cross-platform PostHog `use-transcription-service-v2` flag, which
 * drives only the read-side transcript merge.
 *
 * Read at session start and locked for the lifetime of that recording — see
 * `useMutateStartTauriRecording` and `useChronicleStartRecording` for the lock
 * sites.
 */
export const useIsAppsUseTranscriptionServiceV2DesktopEnabled = () => {
  return useBooleanFlagValue('release-apps-use-transcription-service-v2-desktop', false);
};

/**
 * Flagsmith: APP-6508 — route desktop online (mic) recording chunk uploads
 * through Rust (the `online_chunk_upload` pipeline) instead of fetching the
 * bytes back over IPC and uploading from the webview. Off by default. Read at
 * session start and locked for the lifetime of that recording — see
 * `useMutateStartTauriRecording` and the `useRustChunkUpload` lock in
 * `tauriRecordModel`. The FE fetch-and-upload path remains the fallback.
 *
 * ⚠️ DO NOT RETIRE THIS FLAG WITHOUT READING THIS FIRST (DSK-719).
 *
 * This flag is currently the ONLY lever over three unrelated DSK-719
 * behaviours it happens to arm: the skipped safe-start speaker delay, the
 * deferred SCK speaker re-init, and WAV rotation + deferred backup extraction.
 * If a clinician loses speaker audio or a consult backup in the field, the only
 * remedy today is disabling the entire Rust chunk-upload engine or shipping a
 * new build.
 *
 * `release-` flags are cleaned up at 100%, and a cleanup PR removing this will
 * NOT look like it is removing a safety net — which is exactly how the path
 * ends up permanently ungated. So a dedicated `op-` kill switch around those
 * three behaviours must land BEFORE this flag is retired. Raised in review by
 * Jojo-hmg on PR #19699; deliberately filed as follow-up rather than built in
 * that PR, because a kill switch not yet provisioned in the flag backend reads
 * the compiled default and gives everyone a lever that does nothing.
 */
export const useIsAppsRustChunkUploadDesktopEnabled = () => {
  return useBooleanFlagValue('release-apps-rust-chunk-upload-desktop', false);
};

/**
 * Flagsmith: APP-8605 — kill switch for the zombie-recorder takeover in the
 * desktop converge-before-start path. OFF (and the default when Flagsmith is
 * unreachable) restores the APP-6140 behaviour: an owned non-Stopped recorder
 * is never force-stopped; start() rejects instead.
 */
export const useIsAppsZombieRecorderTakeoverDesktopEnabled = () => {
  return useBooleanFlagValue('release-apps-zombie-recorder-takeover-desktop', false);
};

const WINDOWS_SYSTEM_AUDIO_CAPTURE_FLAG_KEY =
  'release-apps-windows-system-audio-capture-desktop';
const WINDOWS_COMMS_DEFAULT_MIC_FLAG_KEY =
  'release-apps-windows-comms-default-mic-desktop';
const APPS_LOW_DISK_WARNING_DESKTOP_FLAG_KEY = 'release-apps-low-disk-warning-desktop';

/**
 * Non-hook tri-state read for non-React code (device restore / pre-selection
 * in `scribeRecordModel.findActiveTauriDevice`).
 *
 * Never a bare boolean: those paths can run before the flag provider
 * hydrates, and a pre-hydration read must surface as `'unknown'` — treating
 * it as OFF would persist a downgrade (a lost "System audio" selection, or a
 * first-run "first device" pick that permanently opts out of the smart
 * default). The start-time resolver remains the enforcement point for what
 * reaches Rust.
 */
const getWindowsDesktopFlagGate = (flagKey: string): 'on' | 'off' | 'unknown' => {
  // Platform POLICY stays app-side (P3 F2, design D8): the door's gate is
  // deliberately platform-neutral, and without this short-circuit the web
  // build would read 'unknown'/live values for desktop-only flags.
  if (!isTauri()) return 'off';
  return getFlagGate(flagKey);
};

/**
 * Start-time variant: settles a pre-hydration `'unknown'` by waiting (bounded)
 * for the provider to become READY instead of treating the static flag
 * default as a real OFF — a recording started right after app launch must not
 * silently downgrade a flag-ON selection for the whole recording. Timeout /
 * provider ERROR resolve to `'off'`, the kill-switch-conservative default.
 */
const resolveDesktopFlagGate = async (
  flagKey: string,
  timeoutMs: number
): Promise<'on' | 'off'> => {
  // Same app-side platform policy as above; the ProviderEvents
  // wait-with-timeout machinery lives in the door (P3 F2).
  if (!isTauri()) return 'off';
  return resolveFlagGate(flagKey, timeoutMs);
};

/** Tri-state gate for the whole-system speaker capture flag (see above). */
export const getWindowsSystemAudioCaptureGate = (): 'on' | 'off' | 'unknown' =>
  getWindowsDesktopFlagGate(WINDOWS_SYSTEM_AUDIO_CAPTURE_FLAG_KEY);

/** Start-time gate for the whole-system speaker capture flag (see above). */
export const resolveWindowsSystemAudioCaptureGate = async (
  timeoutMs = 2000
): Promise<'on' | 'off'> =>
  resolveDesktopFlagGate(WINDOWS_SYSTEM_AUDIO_CAPTURE_FLAG_KEY, timeoutMs);

/** Tri-state gate for the comms-default mic flag (see above). */
export const getWindowsCommsDefaultMicGate = (): 'on' | 'off' | 'unknown' =>
  getWindowsDesktopFlagGate(WINDOWS_COMMS_DEFAULT_MIC_FLAG_KEY);

/** Start-time gate for the comms-default mic flag (see above). */
export const resolveWindowsCommsDefaultMicGate = async (
  timeoutMs = 2000
): Promise<'on' | 'off'> =>
  resolveDesktopFlagGate(WINDOWS_COMMS_DEFAULT_MIC_FLAG_KEY, timeoutMs);

/**
 * Flagsmith: APP-7915 WS0 — Windows whole-system speaker capture (Application
 * Loopback in EXCLUDE mode, own PID excluded) replacing the eConsole
 * device-level loopback that misses eCommunications-role telehealth audio.
 * Kill switch, not an experiment — do NOT add `useManualExposure`. Default OFF.
 * Rollout: co-equal priority with `release-apps-silent-capture-warning-desktop`
 * (flag C), which is the only fix for explicit-device telehealth users.
 *
 * When ON (every consumer also gates on `isWindows`), recording start resolves
 * a "System audio" / system-default speaker selection to the `__SYSTEM_WIDE__`
 * sentinel and the speaker dropdown shows the "System audio" entry. Explicit
 * device/app selections are always respected. Rust never reads the flag —
 * capture arms purely on receiving the sentinel, so an OFF flip takes effect
 * at the next recording start.
 */
export const useIsAppsWindowsSystemAudioCaptureDesktopEnabled = () => {
  return useBooleanFlagValue(WINDOWS_SYSTEM_AUDIO_CAPTURE_FLAG_KEY, false);
};

/**
 * Flagsmith: APP-7915 WS3 — resolve the `__SYSTEM_DEFAULT__` mic sentinel to
 * the Windows COMMUNICATIONS-default capture device (the headset every calling
 * app uses) instead of the console default (built-in mic array). Explicit
 * device selections are never overridden. Kill switch, not an experiment — do
 * NOT add `useManualExposure`. Default OFF. Rollout: never ahead of
 * `release-apps-silent-capture-warning-desktop` (flag C is its visibility net).
 */
export const useIsAppsWindowsCommsDefaultMicDesktopEnabled = () => {
  return useBooleanFlagValue(WINDOWS_COMMS_DEFAULT_MIC_FLAG_KEY, false);
};

/**
 * Flagsmith: APP-7915 WS4 (APP-7961) — in-recording silent-capture warning UI
 * (mic + speaker actionable toasts, persistent recording-surface indicator, OS
 * notification when backgrounded), driven by the Rust silence monitor's
 * AUDIO_CAPTURE_ACTIVITY events (WS1, APP-7958). Gates the UI only — the
 * ipc.ts listener and `captureSilence` state stay live so an ON flip
 * mid-recording takes effect immediately.
 * Kill switch, not an experiment — do NOT add `useManualExposure`. Default OFF.
 *
 * HARD RELEASE DEPENDENCY: do not enable until WS1 (APP-7958, the Rust
 * silence-monitor emitter) has shipped — without it nothing fires, and
 * enabling early just exposes untested UI paths.
 * Rollout: co-equal priority with flag A (`release-apps-windows-system-audio-
 * capture-desktop`); `release-apps-windows-comms-default-mic-desktop` (flag B)
 * must never roll out ahead of this flag.
 *
 * Rollout interaction with flag A: once whole-system capture is fully rolled
 * out (semantics C — every device selection resolves to `system_wide`), users
 * on a healthy EXCLUDE-mode capture structurally cannot hit the speaker
 * warning (all rendered audio is captured, like macOS). Residual
 * `silent_speaker` funnel volume at that point indicates the WS0 fallback tier
 * (`default_endpoint_loopback`) or a wrong per-app INCLUDE selection — both
 * populations this warning is still the fix for.
 */
export const useIsAppsSilentCaptureWarningDesktopEnabled = () => {
  return useBooleanFlagValue('release-apps-silent-capture-warning-desktop', false);
};

/**
 * DSK-82: gates the Windows native `audio:device_list_changed` push-event
 * sync (`useWindowsAudioDeviceEventSync`) that replaces the idle device-list
 * poll, and (via `set_audio_device_watcher_enabled`) arms the Rust watcher.
 * Flagsmith — the flag lives in `oscerai/infra` `shared_infra/flags/main.tf`
 * (PR #2175). Default OFF — idle polling keeps running on Windows until this
 * rolls out. Kill switch, not an experiment — do NOT add `useManualExposure`.
 */
export const useIsWindowsAudioEventSyncEnabled = () => {
  return useBooleanFlagValue('release-desktop-windows-audio-event-sync', false);
};

/**
 * Flagsmith: remote kill switch for the WAV-writer failure recovery.
 * `WavWriterFailureRecovery` auto-stops the recording on a detected WAV-writer
 * failure and, via `wavWriterFailed`, skips backup/chunk upload and note
 * generation. A false positive irreversibly aborts a recording and discards the
 * salvage path, so this behavior ships gated OFF on a slowly-patching desktop
 * client. The decision is passed to Rust and locked when capture starts, so a
 * mid-recording flag flip cannot split native shutdown from frontend state
 * convergence. The listener stays live; Rust emits/exposes health only for a
 * capture that started with the gate ON.
 * Kill switch, not an experiment — do NOT add `useManualExposure`. Default OFF.
 */
export const useIsAppsWavWriterRecoveryDesktopEnabled = () => {
  return useBooleanFlagValue(WAV_WRITER_RECOVERY_FLAG_KEY, false);
};

/**
 * Flagsmith: gates the proactive low-disk-space warning for desktop captures.
 * When ON, capture start arms a native 30s disk-space monitor that emits
 * `RECORDING_DISK_SPACE_LOW` (warn-only, never blocks the start) once free
 * space on the audio volume drops below the threshold, and the warning banner
 * renders from that event. The decision is passed to Rust and locked
 * when capture starts, so a mid-capture flag flip cannot half-arm the monitor.
 * Rollout/kill gate, not an experiment — do NOT add `useManualExposure`.
 * Default OFF; an absent flag resolves false and keeps everything dark.
 */
// This barrel is where per-flag hooks are declared by convention (see
// AGENT.md), and it contains no components — the react-refresh concern does
// not apply. The durable fix is renaming the barrel to .ts (it has no JSX).
// eslint-disable-next-line react-refresh/only-export-components
export const useIsAppsLowDiskWarningDesktopEnabled = () => {
  return useBooleanFlagValue(APPS_LOW_DISK_WARNING_DESKTOP_FLAG_KEY, false);
};

/**
 * Capture-start resolver for the low-disk monitor. Waits briefly for a
 * pre-hydration provider so an ON rollout cannot be locked OFF for a whole
 * recording merely because the user pressed start immediately after launch.
 */
export const resolveAppsLowDiskWarningDesktopGate = async (
  timeoutMs = 2000
): Promise<'on' | 'off'> =>
  resolveDesktopFlagGate(APPS_LOW_DISK_WARNING_DESKTOP_FLAG_KEY, timeoutMs);

/**
 * Flagsmith: SCR-4287 per-toggle copy settings (headings / bold-italics /
 * bullets / tables) replacing the legacy `default_copy_type` 3-radio UI.
 */
export const useIsImprovedCopyActionsEnabled = () => {
  return useBooleanFlagValue('release-scribe-improve-copy-actions', false);
};

/**
 * Gates the template community's profile-derived default filters on
 * /templates (location = profile country, specialty = profile specialty).
 * OFF (default): the gallery opens unfiltered for everyone — the pre-rollout
 * behaviour. Logged-out visitors are unfiltered regardless of the flag.
 * Rollout/kill gate, not an experiment — do not add useManualExposure.
 */
export const useIsCommunityDefaultFiltersEnabled = () => {
  return useBooleanFlagValue('release-scribe-enable-community-default-filters', false);
};

/**
 * INC-190 / SCR-4531: Gates the `<StuckLoadingSentinel />` component rendered
 * inside `SessionLoadingPage`. When ON, the sentinel emits a Datadog warning
 * every 10s (capped at 15 ticks) while the loading skeleton is mounted.
 * Default OFF; flip per-environment in Flagsmith.
 */
export const useIsStuckLoadingSentinelEnabled = () => {
  return useBooleanFlagValue('release-scribe-stuck-loading-sentinel', false);
};

/**
 * Kill switch for the note tab's forced session refetch on mount. When ON the
 * extra GET is skipped; the clobber guards stay on, so the only effect is a
 * stale display on soft-nav, never the structured-note rewrite. Default OFF.
 * Sibling: `op-scribe-disable-session-switch-refetch` (session-switch GET).
 */
export const useIsNoteSessionRefetchKilled = () => {
  return useBooleanFlagValue('op-scribe-disable-note-session-refetch', false);
};

/**
 * Kill switch for the session page's forced session refetch on switch. When ON
 * the transition GET is skipped, so switching sessions may serve stale details
 * again. Sibling: `op-scribe-disable-note-session-refetch` (Note-tab mount
 * GET); flip both to shed all forced session GETs. Default OFF.
 */
export const useIsSessionSwitchRefetchKilled = () => {
  return useBooleanFlagValue('op-scribe-disable-session-switch-refetch', false);
};

/**
 * Kill switch for the session page's forced session refetch on mount. When ON
 * the route-remount GET is skipped, so returning to a session may serve a
 * cache entry up to an hour old. Siblings:
 * `op-scribe-disable-note-session-refetch` (Note-tab mount GET) and
 * `op-scribe-disable-session-switch-refetch` (session-switch GET). Default OFF.
 */
export const useIsSessionMountRefetchKilled = () => {
  return useBooleanFlagValue('op-scribe-disable-session-mount-refetch', false);
};

/**
 * Kill switch for the level-triggered active-recording nav-guard reconciler
 * (`useActiveRecordingGuardReconciler`). OFF (default): reconciler mounted —
 * releases a leftover `activeSessionIdAtom` when the session note is COMPLETED
 * with a non-blank result. ON: break-glass — unmount the reconciler so a fleet
 * misfire of the release heuristic can be disabled without rollback. Evaluated
 * in `_app.tsx`. Kill gate, not an experiment — do not add useManualExposure.
 *
 * Not gated behind `release-apps-remote-sync-async-web`: that would leave
 * silent-decline leftovers unfixed when the hold flag is off.
 */
export const useIsRecordingGuardReconcilerKilled = () => {
  return useBooleanFlagValue('op-scribe-disable-recording-guard-reconciler', false);
};

/**
 * Kill switch for the Evidence linked-context provenance gate
 * (`resolveAttachableLinkedContext`): sends refuse staged session/patient ids
 * whose `linkedContextOriginAtom` belongs to a DIFFERENT chat — the fix for
 * new chats born linked to the previously viewed session's patient. The gate
 * relies on every staging writer stamping the origin, so if a missed writer
 * ever leaves legitimate staging refused (context silently not attaching),
 * flip this ON to bypass the origin check and restore the pre-gate raw-atom
 * reads without a deploy. Default OFF (gate active). Kill gate, not an
 * experiment — do not add useManualExposure.
 */
export const useIsLinkedContextProvenanceGateKilled = () => {
  return useBooleanFlagValue('op-evidence-disable-linked-context-provenance-gate', false);
};

/**
 * Kill switch for note-editor HTML escaping. Default OFF: the note editor leaves
 * Ask Heidi / Evidence HTML unescaped so it renders. Flip ON to re-enable
 * angle-bracket escaping in the note editor (the prior behaviour) if the render
 * fix regresses. Kill gate, not an experiment — do not add useManualExposure.
 */
export const useIsNoteHtmlEscapeEnabled = () => {
  return useBooleanFlagValue('release-scribe-note-html-escape', false);
};

/**
 * APP-6669 — UMBRELLA gate for the ENTIRE offline-recording frontend surface.
 *
 * ALL offline-recording UI MUST gate behind this single hook: the connectivity
 * banner today, plus future sync-state badges, the recording entry point, and any
 * sync panel/settings. New offline-recording UI? Wrap it in this. See
 * `specs/010-offline-recording/` and the CLAUDE.md convention note.
 *
 * Default OFF. The connectivity atom reports `offline` on every desktop build until
 * the Rust monitor's probe target (`set_target`) is wired (deferred to a later
 * phase), so an ungated surface shows a false "you're offline" banner. Keep OFF
 * until `set_target` is live + verified; flip per-environment in Flagsmith (staging
 * first). This is a rollout/kill gate, NOT an experiment — do not add useManualExposure.
 *
 * Gates user-visible UI only; the headless connectivity/event providers
 * (`OfflineConnectivityProvider`, `useOfflineRecordingEvents`) run unconditionally —
 * they are isTauri-guarded, no-op without a probe target, and render nothing.
 *
 * NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS only patches PostHog, not OpenFeature,
 * so honour it explicitly here (mirrors the patient-list hooks) — otherwise local
 * devs on force-all can never see any offline-recording UI.
 */
export const useIsOfflineRecordingEnabled = () => {
  // Lite: read inside shared recording hooks by many components — see `useBooleanFlagLite`.
  const fromFlagsmith = useBooleanFlagLite(OFFLINE_RECORDING_FLAG_KEY, false);
  const fromOfflineCache = useCachedOfflineRecordingFlagFallback();
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith || fromOfflineCache;
};

/**
 * DSK-885: kill switch for the desktop single-external-link-owner path — the
 * global capture-phase click handler plus the `window.open` override that
 * together own "an external link opens the OS browser".
 *
 * Defaults ON, unlike almost every flag here, because OFF is not a rollback.
 * The per-component handlers this replaced are deleted, so OFF leaves only the
 * listener `tauri-plugin-opener` injects: no `window.open` interception
 * (Intercom opens inside the webview) and no bare `mailto:`/`tel:`, since that
 * listener only claims anchors carrying `target="_blank"`. An unresolved or
 * unreachable provider therefore has to read as ON, or a Flagsmith outage
 * would strand every desktop session in a state that has never shipped.
 *
 * Flip OFF only to stop a link-handling regression without waiting for a
 * desktop build, and prefer fixing forward. Kill gate, not an experiment — do
 * not add `useManualExposure`. `FORCE_ENABLE_ALL_FEATURE_FLAGS` is not honoured
 * here: it exists so a default-OFF feature is visible locally, and this one is
 * already on.
 */
export const useIsSingleExternalLinkOwnerEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(
    'op-apps-single-external-link-owner-desktop',
    true
  );
  if (!isTauri()) return false;
  return fromFlagsmith;
};

/**
 * default OFF — not an experiment, so no `useManualExposure`.
 */
export const useIsWarnBeforeWebViewReloadEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(
    'release-apps-warn-webview-reload-desktop',
    false
  );
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

export const useIsAppointmentBarEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(
    'release-apps-appointment-bar-desktop',
    false
  );
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/** Flagsmith: gates the forced dialog for users with an integration but no active integration access. */
/**
 * Gates the revert-integration confirmation dialog on the integrations settings
 * page.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`.
 */
export { useIsRevertIntegrationEnabled } from '@heidi/integrations/flags';

/**
 * Whether the OpenFeature/Flagsmith provider has settled (READY or ERROR —
 * anything other than NOT_READY / RECONCILING). While `false`, flag hooks
 * still return their defaults, so callers that make irreversible decisions on
 * a flag value (e.g. the Healthdirect launch flow) should wait for `true`
 * before acting rather than treating an unresolved default as a settled read.
 * ERROR counts as resolved so such callers fall back to the flag default
 * instead of waiting forever. (Implementation moved verbatim into the
 * `@heidi/runtime/feature-flags` door at the F-series — P3 F2/F4.)
 */
export { useAreFeatureFlagsResolved } from '@heidi/runtime/feature-flags';

/**
 * Internal diagnostics rollout. Default OFF. Provider ERROR / slow-resolve
 * diagnostics are separately gated by
 * {@link useIsClientSdkSelfReportKilled} so this flag alone is not a
 * complete kill switch.
 */
export const useIsClientSdkSelfReportEnabled = () => {
  return useBooleanFlagValue('release-scribe-client-sdk-self-report', false);
};

/**
 * Fail-closed kill switch for client SDK self-report. Default ON (killed):
 * suppresses happy-path and Flagsmith ERROR/timeout reports so a slow
 * provider on deploy cannot generate ungated diagnostics traffic. Flip OFF
 * in Flagsmith only when diagnostics should be allowed. Kill gate, not an
 * experiment — do not add useManualExposure.
 */
export const useIsClientSdkSelfReportKilled = () => {
  return useBooleanFlagValue('op-scribe-disable-client-sdk-self-report', true);
};

/**
 * ANZ-1328: Gates the reworked Healthdirect launch flow (per-launch guards +
 * purge-all-onboarding-sessions-then-create, stopgap while the backend can't
 * handle duplicate onboarding sessions). OFF (default) runs the legacy flow
 * exactly as shipped on main (`useOnboardingHealthDirectLegacy`). Callers
 * must pair this with {@link useAreFeatureFlagsResolved} before acting — an
 * unresolved provider reads as the default `false`. Temporary: retire once
 * the v2 flow is validated and the backend enforces onboarding-session
 * uniqueness.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`. The
 * `useAreFeatureFlagsResolved` pairing is the CALLER's, not this getter's, which
 * is why the body was a plain read and could move.
 */
export { useIsHealthdirectLaunchV2Enabled } from '@heidi/integrations/flags';

/**
 * Kill switch for forcing the nav sidebar collapsed on every HealthDirect
 * launch (the HD iframe is space-constrained). Default OFF: the
 * collapse-on-open behaviour is active. Flip ON to disable it and restore the
 * persisted expand/collapse preference for HD launches, if the override
 * regresses. Kill gate, not an experiment — do not add useManualExposure.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`. Claimed by that domain on
 * the STORY rather than the namespace: `op-scribe-` is the repo's generic
 * kill-switch prefix and carries no ownership signal, and what this gates is the
 * HealthDirect launch whose sibling flag is unambiguously integrations-owned.
 */
export { useIsHealthdirectSidebarCollapseKilled } from '@heidi/integrations/flags';

/**
 * Country-scoped EHR selection: the full-list EHR/EMR dropdowns fetch their
 * options from template-gallery's `integration-providers` endpoint instead of
 * enumerating the generated `EHRProvider` enum.
 */
export { useIsCountrySpecificEhrSelectionEnabled } from '@heidi/integrations/flags';

/**
 * Gates the "Workspace name" repurposing of the onboarding / setup / account
 * "Organisation name" field — the relabelled copy, the new placeholder, and the
 * `<first name>'s Workspace` prefill. OFF (default) renders the legacy
 * "Organisation name" experience everywhere.
 *
 * MOVED to `@heidi/workspace` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/workspace/src/flags.ts`.
 */
export { useIsWorkspaceOnboardingRenameEnabled } from '@heidi/workspace/flags';

/**
 * SCR-4585: Gates the 30s deadline on the session-by-id fetch. Non-hook so
 * `useQuerySessionById`'s `queryFn` can read it without binding the flag as a
 * React value — keeps it out of `queryKey` without an exhaustive-deps suppression.
 */
export const isSessionFetchTimeoutEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-session-fetch-timeout',
    false
  );
};

/**
 * v3 chunk kill switch. Non-hook: read once at recording start (after the ledger pipeline
 * pin, so provider readiness is already settled) — the persisted per-recording
 * transport, not this flag, governs every later upload.
 */
export const isTranscribeChunkV3Enabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-transcribe-chunk-v3',
    false
  );
};

/**
 * When on, personalisation apply + learn are off by default and the user must
 * explicitly opt in via the personalisation modal. Non-hook so it can be
 * called from pure helpers (e.g. `isPersonalizationGateOpen`).
 */
export const isPersonalizationOptInEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-personalisation-opt-in',
    false
  );
};

/**
 * SCR-4952: When enabled, DOCUMENT templates generate a consult note via the
 * standard note-generation flow instead of routing through the
 * document-generation flow. Non-hook so pure helpers (e.g.
 * `isDocumentGenerationTemplate`) and post-recording callbacks can read it
 * imperatively.
 */
export const isGenerateConsultNoteFromDocumentTemplateEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-generate-consult-note-from-document-template',
    false
  );
};

/**
 * Demo flow simplification — "Create a test session" header copy and no
 * StartButton on the entry-point overlay.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out.
 */
export { useIsDemoFlowSimplificationEnabled } from '@heidi/growth/flags';

/**
 * The PDF template demo on the community template details screen.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out.
 */
/**
 * Gates DOCX template upload and DOCX export for form filling.
 * Using PostHog instead of Flagsmith (GRO — rollout reliability).
 */
export const useIsDocxFormEnabled = () => {
  return useFeatureFlagEnabled('release-growth-enable-docx-form') ?? false;
};

/**
 * Gates XML form templates: `.xml` uploads in the form-template dropzone, the
 * XML review step, and the XML session document view. Flagsmith via
 * OpenFeature; on-prem it resolves from the baked bundle (pinned on).
 *
 * MOVED to `@heidi/templates` by ruling 36's whole-class fan-out — templates' by
 * surface, and the key keeps its `release-growth-` spelling because a flag key
 * is a frozen external identity.
 *
 * Add / delete / re-type in the form-DSL editor rides this same flag
 * (MOD-1643) — there is no dedicated structural-edit key.
 */
export { useIsXmlFormEnabled } from '@heidi/templates/flags';

/**
 * Gates JSON form templates: `.json` uploads in the form-template dropzone, the
 * JSON review step, source and field edits, and Community publishing. Viewing a
 * filled JSON session document is not gated. Flagsmith via OpenFeature, default off.
 *
 * MOVED to `@heidi/templates` alongside the XML flag; the key keeps the same
 * `release-growth-` spelling.
 */
export { useIsJsonFormEnabled } from '@heidi/templates/flags';

/**
 * Withholds PDF form templates. Pinned on by the on-prem bundle: the
 * appliance's gateway is text-only and cannot fill a PDF form.
 */
export { useIsPdfFormDisabled } from '@heidi/templates/flags';

/**
 * GRO-3522: gates the full-page letterhead editor flow (route + Appearance entry).
 * Using PostHog instead of Flagsmith (GRO — rollout reliability), matching
 * `useIsDocxFormEnabled`.
 */
export const useIsLetterheadEditorEnabled = () => {
  return useFeatureFlagEnabled('release-growth-enable-letterhead-editor') ?? false;
};

const FIRST_NOTE_TEMPLATE_SELECTION_FLAG_KEY =
  'release-growth-enable-first-note-template-selection';

/**
 * Gates the first-note template-selection modal for new users.
 * PostHog (GRO — rollout reliability)
 * Rollout/experiment gate. Default OFF.
 */
export const useIsFirstNoteTemplateSelectionEnabled = () => {
  return useFeatureFlagEnabled(FIRST_NOTE_TEMPLATE_SELECTION_FLAG_KEY) ?? false;
};

/** Non-hook accessor for the launch gate that runs outside React. */
export const IS_FIRST_NOTE_TEMPLATE_SELECTION_ENABLED = () =>
  posthog.isFeatureEnabled(FIRST_NOTE_TEMPLATE_SELECTION_FLAG_KEY) ?? false;

/**
 * The Heidi Remote intro promos, one flag per cohort — MONTHLY
 * Scribe-Plus/Clinician (CTA routes to the in-app annual-upgrade flow), YEARLY
 * (loyalty pricing, CTA routes to the storefront) and FREE/trialling (the
 * bundle). The component layer also checks the offer cutoff, so a stale flag
 * can't keep a promo visible.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out. All three are
 * declared `builds: 'cloud-only'` there, measured: their one eligibility hook
 * opens with `if (isOnPrem) return null`.
 */
export {
  useIsRemoteIntroPromoFreeEnabled,
  useIsRemoteIntroPromoMonthlyEnabled,
  useIsRemoteIntroPromoYearlyEnabled,
} from '@heidi/growth/flags';

/**
 * Clinical coding dashboard / case surfaces.
 *
 * MOVED to `@heidi/coding` by ruling 36's fan-out batch 1. The
 * key/default pair is declared and read once in `packages/coding/src/flags.ts`,
 * co-located as the 2026-08-29 amendment requires; this line is the re-export
 * that keeps the barrel's importers — and their `jest.mock('@/features/flags')`
 * literals — resolving unchanged.
 *
 * Coding was the last domain reading a descriptor it declared in
 * `/contributions` from the app barrel, which is the arrangement the amendment
 * superseded. Deleting the `@heidi/coding/contributions` import at the top of
 * this file was the other half of the change: this barrel is reached from tauri
 * boot paths, and it used to pull coding's whole contributions door — the
 * settings-screen descriptor included — to read one boolean.
 */
export { useIsClinicalCodingDashboardEnabled } from '@heidi/coding/flags';

/**
 * The session problem set as its own panel below the note (read from the
 * `problem-set` read model). Declared and read in
 * `packages/integrations/src/flags.ts` with the `./problem-charting` door it
 * gates; re-exported here so the note tab keeps one import site for its flags.
 */
export { useIsProblemSetPanelEnabled } from '@heidi/integrations/flags';

/**
 * Flagsmith: the capability-driven push-note button (default variant), its
 * Magentus arm, and the capability-driven push-DOCUMENT button on the document
 * tab.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`. The three keys are
 * suffix-related (`…-push-note-button`, `…-push-note-button-magentus`,
 * `…-push-document-button`), which is why the package's suite spells all three
 * out rather than sampling one.
 */
export {
  useIsCapabilityDrivenPushDocumentButtonEnabled,
  useIsCapabilityDrivenPushNoteButtonEnabled,
  useIsCapabilityDrivenPushNoteButtonMagentusEnabled,
  useIsCernerPushNoteOptionsEnabled,
  useIsUnlinkedSectionsPushWarningEnabled,
} from '@heidi/integrations/flags';

/** ChenMed Phase 1 in-session protocol nudges (PIN protocol cards). */
export const useIsInSessionNudgesChenmedEnabled = () => {
  return useBooleanFlagValue('release-ml-scribe-in-session-nudges-chenmed', false);
};

/**
 * Imperative read of the ChenMed nudges flag for non-React contexts (e.g. the
 * scribe-record Rematch model, which routes task generation). Mirrors
 * `useIsInSessionNudgesChenmedEnabled`.
 */
export const getIsInSessionNudgesChenmedEnabled = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-ml-scribe-in-session-nudges-chenmed',
    false
  );

/**
 * Reusable in-session guidance surface (cards / chat / red flags / trials).
 * Parallel to ChenMed PIN nudges — does not replace or gate ChenMed. The
 * imperative half is for non-React paths (the agent `ui.session_surface`
 * handler) and fails closed when OpenFeature is unset.
 *
 * MOVED to `@heidi/evidence` by ruling 36's whole-class fan-out — hook and
 * imperative reader off ONE descriptor, the second such pair on that door. The
 * imperative body's own `OpenFeature.getClient(FEATURE_FLAG_DOMAIN)` became the
 * door's `readFlag`, which resolves the same client on the same default domain.
 */
export {
  getIsInSessionSurfaceEnabled,
  useIsInSessionSurfaceEnabled,
} from '@heidi/evidence/flags';

/**
 * Feature flag for the external-share **inline expand** UI inside the share modal.
 *
 * When ON, clicking the "External access" empty state expands a full inline form
 * (recipients chip input + scope + expiry) and submits directly — replacing the
 * dashed hint button that used to just focus the user search input and funnel the
 * user through a secondary AlertDialog.
 *
 * Gated independently from session link sharing so we can
 * ship the UX redesign to a subset of orgs already on link sharing.
 */
export const useIsExternalShareInlineExpandEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-external-share-inline-expand') ?? false;
};

/**
 * Feature flag for the external-share **reveal entry** redesign inside the
 * share modal's "External access" section.
 *
 * When ON, the section shows a single explainer card ("Share with people
 * outside your organisation") that reveals an inline email field on click;
 * sending hands off to the external-share confirmation dialog (scope + expiry).
 * When OFF, the legacy hint button is shown instead.
 *
 * PostHog (not Flagsmith) so backend evaluation stays consistent, mirroring
 * session link sharing. Gated independently from link
 * sharing so we can ramp this entry-point redesign on its own.
 */
export const useIsExternalShareRevealEntryEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-external-share-reveal-entry') ?? false;
};

/**
 * Feature flag for the merged Share-via-email modal (ShareViaEmailModal).
 *
 * When ON, the More-actions → Send as email entry opens a Share-flavoured modal
 * with a multi-recipient chip input and two checkboxes that default ON:
 *   - Email a copy of the note
 *   - Read-only session link
 *
 * Toggling on the link checkbox also creates an external link-share record
 * (visible from the existing Share Session modal where it can be revoked).
 *
 * Gated independently from session link sharing because
 * this changes the email-modal entry point itself; we want orgs already on link
 * sharing to opt in to the merged flow on a separate rollout cadence.
 */
/** Flagsmith: gates the merged "Email session" modal entry point. */
export const useIsSendAsEmailMergedModalEnabled = () => {
  return useBooleanFlagValue('release-scribe-send-as-email-merged-modal', false);
};

/**
 * Whether the OpenFeature provider has resolved anything yet.
 *
 * Every `useBooleanFlagValue` returns its CODE default until then, which for a
 * release flag is `false`. A route that redirects away on a false flag
 * therefore redirects on the FIRST render of a hard refresh, a deep link or a
 * desktop cold start, before the flag has been read at all, and bounces an
 * enabled user out of a surface they are entitled to. Navigating in from the
 * sidebar never shows it, because the provider is long settled by then.
 *
 * Gate the redirect EFFECT on this, not the render path: rendering a gated
 * surface on a default-false flag is the one thing a kill switch must not do,
 * so `if (!isEnabled) return null` stays as it is and the user sees a blank
 * frame for as long as the provider takes.
 *
 * `RECONCILING` counts as unsettled. It happens when the evaluation context
 * changes (a login, a workspace switch), and holding a redirect for a moment
 * is cheaper than firing one on a value that is about to change. A provider in
 * `ERROR` counts as settled: the defaults are then the real answer.
 *
 * Mirrors `DefaultsSettings.tsx` / `DictateSettings.tsx`, which wait on the
 * same two statuses before trusting a flag.
 */
export const useAreFeatureFlagsSettled = () => {
  const status = useOpenFeatureClientStatus();
  return status !== ProviderStatus.NOT_READY && status !== ProviderStatus.RECONCILING;
};

export const useIsRepeatsPocEnabled = () => {
  // Local-dev escape hatch: NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS only patches
  // PostHog, not OpenFeature/Flagsmith, so honour it explicitly (mirrors
  // useIsApolloPrescriptionCategoryEnabled / useIsPatientListEnabled). Remove once
  // an OpenFeature-wide force-all provider exists.
  const fromFlagsmith = useBooleanFlagValue('release-scribe-enable-repeats-poc', false);
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Apollo triage POC — gates the `/triage` master/detail review dashboard and
 * its sidebar nav entry. Flagsmith flag mirrors the backend
 * `release-scribe-enable-triage-poc`. Default OFF so the clinical-review surface
 * never renders for users/regions outside the rollout (sibling of
 * {@link useIsRepeatsPocEnabled}). Every triage UI consumer must call this hook
 * before rendering.
 */
export const useIsTriagePocEnabled = () => {
  // Local-dev escape hatch: NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS only patches
  // PostHog, not OpenFeature/Flagsmith, so honour it explicitly (mirrors
  // useIsApolloPrescriptionCategoryEnabled / useIsPatientListEnabled). Remove once
  // an OpenFeature-wide force-all provider exists.
  const fromFlagsmith = useBooleanFlagValue('release-scribe-enable-triage-poc', false);
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Apollo concierge POC: gates the `/concierge` page and its sidebar entry.
 * Mirrors the backend `release-scribe-enable-apollo-concierge-poc`, which also
 * gates the /concierge-requests router the page reads from.
 */
export const useIsConciergePocEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(
    'release-scribe-enable-apollo-concierge-poc',
    false
  );
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Apollo prescription-category slice — gates:
 *   - the `prescription` option in the clinical-task category dropdown
 *   - the inline prescription form rendered on tasks with
 *     `category === "prescription"`
 *   - calls to `/api/v2/prescriptions/*` endpoints
 *
 * Flagsmith flag mirrors the backend `IS_APOLLO_PRESCRIPTION_CATEGORY_ENABLED`
 * helper (ml-scribe `features/release/release_apollo_prescription_category.py`).
 * Default OFF — every consumer must call this hook before rendering
 * prescription-aware UI.
 *
 * MOVED to `@heidi/tasks` by ruling 36's fan-out batch 1 — declared
 * and read once in `packages/tasks/src/flags.ts`.
 *
 * RECLASSIFIED TO TASKS in that move, which is worth recording because the key
 * argues the other way: `release-apollo-prescription-category` reads coding-ish
 * by prefix. Ownership follows the SURFACE — all five production consumers are
 * clinical-task surfaces, and they are inside `packages/tasks` now
 * now — so tasks owns it.
 *
 * THE forceAll BRANCH COLLAPSED ON THE MOVE, behaviour-preserving, and it is the
 * removal the deleted comment here asked for ("remove once an OpenFeature-wide
 * force-all provider exists"). One exists: the door's `useBooleanFlag` returns
 * `getFeatureFlagsConfig().forceEnableAll || value`, and
 * `src/utils/runtime-wiring.ts` injects that boolean from the SAME
 * `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` read the branch performed.
 */
export { useIsApolloPrescriptionCategoryEnabled } from '@heidi/tasks/flags';

/**
 * EVI-2279: Persist mid-chat session/patient links via `/chat/completions` and
 * enforce the clinical share gate on web. OFF (default): EVI-2243 soft-link
 * only — outgoing mid-chat ids are not treated as share-disqualifying until
 * the backend documents them. ON: consume BE persist (share gate + refetch).
 *
 * Flagsmith-managed (infra PR #2344) — not PostHog, which is what made it
 * door-readable while its share-chat siblings below are not.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsMidChatClinicalLinkPersistEnabled } from '@heidi/evidence/flags';

/**
 * Feature flag for sharing Evidence chat links.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, on TWO
 * counts. `useFeatureFlagEnabled` resolves through PostHog, which
 * `@heidi/runtime/feature-flags` carries no surface for, so moving it would
 * change WHICH PROVIDER answers (`monorepo-wave-1-p3-feature-flags` Z4). And the
 * two `useManualExposure` pairings make it an EXPERIMENT-backed read: the
 * exposure invariant requires a move to keep the pairing through an injected
 * port on ruling 24's shape, because the emit is an app reaction. Both would have
 * to be answered together, which is a bigger job than a declaration relocation.
 */
export const useIsShareEvidenceChatEnabled = () => {
  const isShareEnabled =
    useFeatureFlagEnabled('release-evidence-enable-share-chat') ?? false;
  const isIntroEnabled =
    useFeatureFlagEnabled('release-evidence-enable-share-chat-intro') ?? false;

  useManualExposure({
    flagKey: 'release-evidence-enable-share-chat',
    isEnabled: isShareEnabled,
    isVisible: true,
  });

  useManualExposure({
    flagKey: 'release-evidence-enable-share-chat-intro',
    isEnabled: isIntroEnabled,
    isVisible: true,
  });

  return isShareEnabled;
};

/**
 * Feature flag for the one-time introduction spotlight that points to the
 * Share chat button in the Evidence chat header. Gated separately from the
 * share feature itself so the intro can be rolled out / rolled back
 * independently (e.g. trial cohorts, gradual release).
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable;
 * see `useIsShareEvidenceChatEnabled` above, which reads the same key.
 */
export const useIsShareEvidenceChatIntroEnabled = () => {
  return useFeatureFlagEnabled('release-evidence-enable-share-chat-intro') ?? false;
};
/*
 * When enabled, allows uploading non-fillable PDFs as templates (My Forms).
 */
export const useIsNonFillablePdfTemplateEnabled = () => {
  return useBooleanFlagValue('release-scribe-enable-non-fillable-pdf-template', false);
};

/**
 * Interactive PDF preview editing in session PDF window.
 * When enabled, desktop users can edit detected PDF fields inline.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 */
export const useIsInteractivePdfPreviewEditingEnabled = () => {
  // return useBooleanFlagValue(
  //   'release-scribe-enable-interactive-pdf-preview-editing',
  //   false
  // );
  return (
    useFeatureFlagEnabled('release-scribe-enable-interactive-pdf-preview-editing') ??
    false
  );
};

/**
 * When enabled, shows the evidence chat retention settings in team evidence settings,
 * allowing team admins to configure automatic deletion of standalone evidence chats.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsEvidenceChatRetentionEnabled } from '@heidi/evidence/flags';

/**
 * When enabled, the "Create Template" modal shows three separate cards
 * (Note, Document, PDF form) instead of the default two-card layout.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for this experiment.
 */
export const useIsThreeCardTemplateModalEnabled = () => {
  return useFeatureFlagEnabled('release-growth-three-card-template-modal') ?? false;
};

/**
 * Hides the "Document" card in the three-card "Create template" type picker,
 * leaving Note + PDF form. Default OFF = Document card shown.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out.
 */
export { useIsDocumentTemplateCardHidden } from '@heidi/growth/flags';

/**
 * Gates the standalone calculators surface — its nav item, the calculator
 * library and the session tools card.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsStandaloneCalculatorsEnabled } from '@heidi/evidence/flags';

/**
 * Gates Medication Check and Guideline Check in the Scribe V2 Tools card
 * (EVI-2332). Default OFF.
 *
 * Declared and read once in `packages/evidence/src/flags.ts`.
 */
export { useIsScribeCdsToolsEnabled } from '@heidi/evidence/flags';

/**
 * DS-957: Gates the redesigned template library data table. Default OFF;
 * flipped per-environment in Flagsmith.
 *
 * MOVED to `@heidi/templates` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/templates/src/flags.ts`.
 */
export { useIsTemplatesTableRedesignEnabled } from '@heidi/templates/flags';

/**
 * GRO-2896: When enabled, unauthenticated users get the chat sidebar signup card below the
 * history scroller with capped list height and a flex spacer so extra vertical space sits
 * under the card, not between the list and the card.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsUnauthChatSidebarSignupCardLayoutEnabled } from '@heidi/evidence/flags';

/**
 * Gates the standalone Patients surface (Phase 0): sidebar nav item, /patients page,
 * and the shared patient drawer.
 *
 * MOVED to `@heidi/patients` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/patients/src/flags.ts`. Ten production consumers, the
 * widest-consumed name in that batch, and every one of them keeps importing it
 * from here.
 *
 * THE forceAll BRANCH COLLAPSED ON THE MOVE, behaviour-preserving, and it is the
 * removal the deleted comment here asked for ("remove the env-var check once an
 * OpenFeature-wide force-all provider exists"). One exists: the door's
 * `useBooleanFlag` returns `getFeatureFlagsConfig().forceEnableAll || value`, and
 * `src/utils/runtime-wiring.ts` injects that boolean from the SAME
 * `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` read the branch performed.
 */
export {
  useIsPatientListEnabled,
  useIsPatientsServerPaginationEnabled,
} from '@heidi/patients/flags';

/**
 * Master gate for the M0 "Send to patient" experience — a clinician emails a
 * secure (password-protected) PDF of a patient-facing document to the patient.
 * PostHog flag so the launch can roll out by percentage (10 -> 30 -> 50).
 * Gates every entry point: the document toolbar Copy/Send split button, the
 * More-menu item, and the modal.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than
 * skipped. This is a `@heidi/calls` flag by noun and it has TWO disqualifiers,
 * either of which alone would hold it:
 *
 *   1. `useFeatureFlagEnabled` resolves through posthog-js on cloud and only
 *      falls back to OpenFeature on the airgapped build.
 *      `@heidi/runtime/feature-flags` carries no PostHog surface, so rewriting
 *      this onto the door would change WHICH PROVIDER answers — a behaviour
 *      change wearing a refactor's clothes. `monorepo-wave-1-p3-feature-flags`
 *      Z4 is the standing rule: a flag consumed from package code must be
 *      door-readable, so extractions steer flags to Flagsmith flag-by-flag.
 *   2. `isTauri()` arrives from `@/tauri/utils`, an app import a package
 *      `flags.ts` may not carry. This half is soluble on its own —
 *      `@heidi/runtime/platform` publishes `isTauri` — so it is the PostHog half
 *      that actually holds the family.
 *
 * Same classification for `useIsSendToPatientNoteEnabled` and
 * `useIsSendToPatientSmsEnabled` below; all three move together when the flags
 * are door-readable.
 */
export const useIsSendToPatientEnabled = () => {
  const enabled = useFeatureFlagEnabled('release-send-to-patient') ?? false;
  // The patient-app flow is web-only for now — never expose it on the desktop
  // (Tauri) app, regardless of the flag's rollout state.
  if (isTauri()) return false;
  return enabled;
};

/**
 * Dedicated rollout and kill gate for note-sourced "Send to patient".
 * The master flag still controls the overall experience; this sub-gate lets
 * notes roll out independently without changing saved-document entry points.
 * This is part of the same web-only patient-app flow as the master gate.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, same two disqualifiers as the master gate
 * above (PostHog is not door-resolvable; `isTauri()` is an app import).
 */
export const useIsSendToPatientNoteEnabled = () => {
  const enabled = useFeatureFlagEnabled('release-send-to-patient-note') ?? false;
  if (isTauri()) return false;
  return enabled;
};

/**
 * Gates the "Auto-generate patient summary" Post Session Defaults toggle in the
 * user and team settings. Mirrors the backend PostHog gate that provisions the
 * Patient Summary tab. Available on web and desktop, so no isTauri guard.
 *
 * Deliberately NOT `release-patient-summary-sms`. Both flag transports write a
 * `flag-<key>` Amplitude user property, so while PostHog and Flagsmith shared
 * that key the stale Flagsmith value (held OFF) clobbered the PostHog rollout
 * value. The old key stays live in both consoles to serve desktop bundles that
 * cannot be updated in place — Flagsmith for v1.47.1 and older, which read it
 * through OpenFeature, and a PostHog alias for v1.48.0 through v1.60.0, which
 * read it through PostHog. Keep the alias's release conditions in step with
 * this flag until those builds age out. The old key is on
 * `AMPLITUDE_FLAG_SYNC_BLOCKLIST` so current builds stop writing its property.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped. This is a
 * `@heidi/patients` flag by noun and the sole member of that domain the fan-out
 * left behind. `useFeatureFlagEnabled` resolves through posthog-js on cloud and
 * only falls back to OpenFeature on the airgapped build;
 * `@heidi/runtime/feature-flags` carries no PostHog surface, so rewriting it
 * onto the door would change WHICH PROVIDER answers — a behaviour change wearing
 * a refactor's clothes. `monorepo-wave-1-p3-feature-flags` Z4 is the standing
 * rule: a flag consumed from package code must be door-readable, so extractions
 * steer flags to Flagsmith flag-by-flag. The dual-console arrangement described
 * above is exactly why that steering is not a one-line change here.
 */
export const useIsPatientSummaryEnabled = () =>
  useFeatureFlagEnabled('release-patient-summary') ?? false;

/**
 * Sub-gate for the SMS delivery method inside "Send to patient". Default OFF
 * for M0 (email-only): the SMS UI and code ship but stay hidden until the SMS
 * backend lands. Flipped on independently of the master flag.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, same two disqualifiers as the master gate
 * above (PostHog is not door-resolvable; `isTauri()` is an app import).
 */
export const useIsSendToPatientSmsEnabled = () => {
  const enabled = useFeatureFlagEnabled('release-send-to-patient-sms') ?? false;
  // Web-only patient-app flow — see useIsSendToPatientEnabled.
  if (isTauri()) return false;
  return enabled;
};

/**
 * Gates the user-facing "uncouple" behavior on patient deletion: when on, the
 * single-patient delete modal becomes a force-pick (keep sessions vs delete
 * sessions) and the same choice surfaces on the Delete All Patients flow.
 * Today's default cascade behavior is preserved when off.
 *
 * Intentionally independent of `release-scribe-enable-patient-list` (that
 * gates the new patient surface) and of `release-scribe-patient-data-retention`
 * (that gates the automated TTL sweeper). User-controlled deletion ramps
 * independently of the autonomous cron.
 *
 * MOVED to `@heidi/patients` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/patients/src/flags.ts`.
 *
 * THE forceAll BRANCH COLLAPSED ON THE MOVE, behaviour-preserving. The body's
 * only composition was a hand-rolled
 * `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` escape hatch; `flags.ts` may not
 * read `process.env` and does not need to, because the door's `useBooleanFlag`
 * returns `getFeatureFlagsConfig().forceEnableAll || value` and
 * `src/utils/runtime-wiring.ts` injects that boolean from the SAME env var.
 */
export { useIsUncouplePatientSessionDeletionEnabled } from '@heidi/patients/flags';

/**
 * Gates the automated patient retention story: TTL sweeper that deletes
 * patients past a per-user threshold, the data-management page UI that
 * configures it, and the org admin override surface that lets teams force
 * a retention policy onto members.
 *
 * Higher blast radius than the uncouple flag (autonomous deletion vs.
 * user-triggered), so ramps on its own cadence. Kill-switch env vars on the
 * BE cron are belt-and-braces beneath this flag.
 *
 * MOVED to `@heidi/patients` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/patients/src/flags.ts`.
 *
 * FIVE OF ITS SEVEN CONSUMERS SIT UNDER `src/features/settings/**` — the user,
 * team and organisation data-management screens plus the notifications form —
 * rather than under a patients root. Those are cross-root READS of a
 * patients-owned flag: they stay exactly where they are and resolve through this
 * re-export. Ownership follows the flag's namespace and the story it gates, not
 * the directory its widest reader lives in.
 *
 * THE forceAll BRANCH COLLAPSED ON THE MOVE, behaviour-preserving — same
 * mechanism as its two siblings above.
 */
export { useIsPatientDataRetentionEnabled } from '@heidi/patients/flags';

/**
 * CAL-1392: Release gate for the Comms data-management settings tab
 * (`/settings/comms-data-management`) and its separately configurable Comms
 * session retention UI. Default OFF — Comms sessions keep the existing blanket
 * retention behaviour until rollout. Not an experiment; no `useManualExposure`.
 *
 * MOVED to `@heidi/calls` by ruling 36's fan-out batch 1 — declared
 * and read once in `packages/calls/src/flags.ts`.
 *
 * THE forceAll BRANCH COLLAPSED ON THE MOVE, behaviour-preserving. The body's
 * only composition was a hand-rolled
 * `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` escape hatch; `flags.ts` may not
 * read `process.env` and does not need to, because the door's `useBooleanFlag`
 * returns `getFeatureFlagsConfig().forceEnableAll || value` and
 * `src/utils/runtime-wiring.ts` injects that boolean from the SAME env var.
 */
export { useIsCommsSessionRetentionEnabled } from '@heidi/calls/flags';

/**
 * Gates the transcript retention row in the **user** data-management settings
 * that configures automatic transcript deletion, mirroring session and patient
 * retention. Default OFF until the backend `transcript_retention_settings`
 * field (SCR-5830) ships — the row must not render before the BE can persist
 * it.
 *
 * The team and org surfaces ramp independently on their own flags — see
 * `useIsTeamTranscriptRetentionEnabled` and `useIsOrgTranscriptRetentionEnabled`.
 *
 * Local-dev escape hatch: respects `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS=true`.
 */
export const useIsTranscriptRetentionEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue('release-scribe-transcript-retention', false);
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Gates the transcript retention row in the **team** data-management settings.
 * Split out from `useIsTranscriptRetentionEnabled` (SCR-5980) so team, org, and
 * user surfaces can be rolled out to whole teams/orgs at their own pace per the
 * rollout plan (SCR-5910). Default OFF.
 *
 * Local-dev escape hatch: respects `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS=true`.
 */
export const useIsTeamTranscriptRetentionEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(
    'release-scribe-transcript-retention-team',
    false
  );
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Gates the transcript retention row in the **org** data-management settings.
 * Split out from `useIsTranscriptRetentionEnabled` (SCR-5980) so team, org, and
 * user surfaces can be rolled out to whole teams/orgs at their own pace per the
 * rollout plan (SCR-5910). Default OFF.
 *
 * Local-dev escape hatch: respects `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS=true`.
 */
export const useIsOrgTranscriptRetentionEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(
    'release-scribe-transcript-retention-org',
    false
  );
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Gates the Projects surface: persistent harness-backed agent runs with
 * Evidence tools + (desktop-only) computer-use + scheduling. Runs on web AND
 * desktop, but the two surfaces ramp on separate flags
 * (`release-scribe-enable-projects`, `release-scribe-enable-projects-web`) so
 * they can be rolled out and killed independently.
 *
 * MOVED to `@heidi/agents` by ruling 36's whole-class fan-out — declared and
 * read once in `packages/agents/src/flags.ts`, where the two descriptors and the
 * `isTauri()` pick live together.
 *
 * IMPORTED, not just re-exported, because four getters BELOW compose it and
 * stay here: `useIsAgentHomeV1Enabled` (PostHog nav-v3),
 * `useIsWorkInSessionEnabled` (noun ambiguous against session),
 * `useIsRpaAgentEnabled` and `useIsRpaRecorderEnabled` (the RPA root is
 * app-shell, roots ruling 2026-08-21). Binding it locally is what lets those
 * compositions keep working while the name is published from its own site —
 * the `useIsWorkspaceSettingsIaEnabled` shape. `./flags` is a headless mechanism
 * door, so this costs the barrel's tauri-boot importers nothing.
 *
 * Its local-dev `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` escape hatch
 * COLLAPSED onto the door's injected `forceEnableAll`, which the app supplies
 * from the same env var at `src/utils/runtime-wiring.ts`.
 */
export { useIsWorkHomeDirectionCTaxonomyEnabled } from '@heidi/agents/flags';
export { useIsProjectsEnabled, useIsSidebarIAEnabled, useIsWorkLetterArtifactEnabled };

/**
 * Contextual thinking phrases (`turn.loader_phrases`): alias of
 * `useIsProjectsEnabled`. The dedicated child flag is no longer read.
 */
export { useIsProjectsContextualLoaderPhrasesEnabled };

/**
 * Gates the experimental run-options selector shown only on project creation
 * surfaces. The selected options are persisted on the created project by
 * ml-scribe as ``run_flags``.
 *
 * MOVED to `@heidi/agents` by ruling 36's whole-class fan-out.
 */
export {
  useIsProjectContinueEnabled,
  useIsProjectDuplicateEnabled,
  useIsProjectRunOptionsEnabled,
  useIsProjectSharingEnabled,
} from '@heidi/agents/flags';

/**
 * Gates the Work home iteration variant (chip-row routines, the journal dock
 * caption, the shrunk New session CTA). PostHog owns the rollout. Package UI
 * takes the resolved boolean as a prop. On-prem falls through to OpenFeature
 * and stays off unless that bundle defines the key. Rollout gate, default OFF.
 */
export const useIsWorkHomeIterationEnabled = () =>
  useFeatureFlagEnabled('release-scribe-work-home-iteration') ?? false;

/** Tokenized chat objects in Work chat. PostHog; the backend reads the same key. */
export const useIsChatObjectRefsEnabled = (): boolean =>
  useFeatureFlagEnabled('release-agents-chat-object-refs') ?? false;

/**
 * Gates the Work composer Fast/Normal speed row in the + menu. Fast sends
 * ``run_flags.fast_mode`` without swapping the work-agent model. Distinct from
 * ``release-scribe-enable-project-run-options`` / ``opus_4_8_fast``.
 */
export const useIsWorkFastModeEnabled = () =>
  useBooleanFlagValue('release-scribe-enable-work-fast-mode', false);

/**
 * Gates the "Leave this chat?" confirm for an unsent Work composer draft.
 * Queued and in-flight sends still confirm regardless. Rollout gate, default OFF.
 */
export const useIsWorkDraftLeaveGuardEnabled = () =>
  useBooleanFlagValue('release-scribe-work-draft-leave-guard', false);

/**
 * Gates the first-call Evidence disclaimer inside Work (the modal that opens
 * the first time Work routes a question to the Evidence sub-agent) and its
 * page mount. Rollout/kill gate for a blocking regulatory interrupt whose copy
 * is still with compliance; default OFF so the surface stays dark until the
 * approved wording lands and the flag is enabled per environment.
 */
export const useIsWorkEvidenceDisclaimerEnabled = () =>
  useBooleanFlagValue('release-scribe-work-evidence-disclaimer', false);

/**
 * Gates the pricing / change-plan modal and the View all plans entry points.
 * PostHog (GRO — rollout reliability), matching the sibling billing gate
 * `IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED`. Rollout gate, default OFF.
 *
 * A hook rather than a `posthog.isFeatureEnabled` thunk: every consumer is a
 * render path, and the thunk does not re-render when the flags land.
 */
export const useIsHeidi2PricingPlansModalEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-pricing-plans-modal') ?? false;

/**
 * PostHog, default OFF. Rollout switch for Heidi 2 credit-usage chrome, Work
 * credit banners, and the send lockout cohort (`heidi_version === 2` as well).
 */
export const useIsHeidi2OptInEnabled = () =>
  useFeatureFlagEnabled(HEIDI_2_OPT_IN_FLAG_KEY) ?? false;

/**
 * Team settings → Access → Heidi 2 (per-member access via
 * `heidi_ii_access_revoked`). PostHog rollout gate, default OFF — sibling
 * of `release-team-evidence-access-controls`. On-prem falls through to
 * OpenFeature via `useFeatureFlagEnabled`.
 */
export const useIsTeamHeidi2AccessControlsEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-team-access-controls') ?? false;

/**
 * Gates Heidi 2 Clinician/Practice **Max** upgrade CTAs. PostHog, default OFF.
 * Standard checkout is live, so free/trial users keep their Standard CTA
 * regardless.
 */
export const useIsHeidi2FullUpgradeFlowEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-full-upgrade-flow') ?? false;

/**
 * Sells Practice Max (MONEY-1128). PostHog, default OFF. ON adds the
 * Standard/Max toggle to the Practice column of the pricing plans modal, routes
 * the Practice Standard and Clinician Max upgrade entries to Practice Max, and
 * restores the sidebar credit-usage tile for Clinician Max seats (GRO-4552 hid
 * it while they had nothing to upgrade to). Recognising an existing Practice
 * Max seat (name, overage behaviour) is not gated.
 */
export const useIsHeidi2PracticeMaxUpgradeEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-practice-max-upgrade') ?? false;

const HEIDI_2_TEAM_UPGRADES_FLAG = 'release-heidi-2-team-upgrades';

/**
 * Heidi 2 team upgrades: the Practice seat card and the Scribe Team
 * Practice Standard card in the pricing plans modal, the
 * `/settings/billing/upgrade` seat checkout, and the Members table Seat column.
 * PostHog, default OFF.
 */
export const useIsHeidi2TeamUpgradesEnabled = () =>
  useFeatureFlagEnabled(HEIDI_2_TEAM_UPGRADES_FLAG) ?? false;

/**
 * {@link useIsHeidi2TeamUpgradesEnabled} without the `false` fallback:
 * `undefined` until the flags load, so a route can wait instead of bouncing.
 */
export const useHeidi2TeamUpgradesFlagState = (): boolean | undefined =>
  useFeatureFlagEnabled(HEIDI_2_TEAM_UPGRADES_FLAG);

const HEIDI_2_MEMBER_UPGRADE_REQUEST_FLAG = 'release-heidi-2-member-upgrade-request';

/**
 * Heidi 2 member upgrade requests: the seat request modal a non-admin team
 * member reaches from the upgrade entry points, and the Max seat dialog the
 * request email opens for a Practice admin on Members. PostHog, default OFF;
 * the PostHog flag depends on `release-heidi-2-team-upgrades`.
 */
export const useIsHeidi2MemberUpgradeRequestEnabled = () =>
  useFeatureFlagEnabled(HEIDI_2_MEMBER_UPGRADE_REQUEST_FLAG) ?? false;

/**
 * {@link useIsHeidi2MemberUpgradeRequestEnabled} without the `false` fallback:
 * `undefined` until the flags load, so a host can wait instead of bouncing.
 */
export const useHeidi2MemberUpgradeRequestFlagState = (): boolean | undefined =>
  useFeatureFlagEnabled(HEIDI_2_MEMBER_UPGRADE_REQUEST_FLAG);

/**
 * Kill switch: ON pauses scheduling Heidi 2 team upgrades (the seat checkout's
 * Schedule action) while billing is reconciled. PostHog, default OFF. ml-scribe
 * reads the same key and refuses the save, so this only explains the refusal.
 */
export const useIsHeidi2TeamUpgradeSchedulingDisabled = () =>
  useFeatureFlagEnabled('disable-heidi-2-team-upgrade-scheduling') ?? false;

/**
 * The Heidi 2.0 first-run onboarding — entry modal and guided tour. Rollout
 * gate, default OFF, sitting under the Heidi II umbrella so onboarding can be
 * held back without touching the opt-in cohort.
 *
 * PostHog, and therefore app-side: `packages/agents` declares its flags through
 * `@heidi/runtime/feature-flags`, which carries no PostHog surface, so moving
 * this getter there would change which provider answers (Z4). The resolved
 * boolean crosses to the package as a prop.
 */
export const useIsHeidi2OnboardingEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-onboarding') ?? false;

/**
 * Mirrors ml-scribe `features/release/enable_agent_credit_allowance_gate.py`
 * (same PostHog key, same Kinde distinct_id). Gates the Clinician Work
 * lockout — grey Send, skipped create/turn POSTs, hidden follow-ups — so
 * FE and BE refuse together or not at all. Rollout gate, fail-open.
 *
 * Two-flag matrix (cohort = `release-heidi-2-opt-in` AND `heidi_version === 2`):
 * - cohort off × gate * : no banners, no lockout (cohort chrome off)
 * - cohort on × gate off: banners/GET still live; Send stays enabled at 100
 * - cohort on × gate on : Clinician lockout at remaining <= 0 (Max complimentary still sends)
 * Flip the gate off first to unblock Work; do not enable the gate wider than
 * the emit cohort. Key is inherited from ml-scribe — do not rename it here.
 */
export const useIsAgentCreditAllowanceGateEnabled = () =>
  useFeatureFlagEnabled('enable-agent-credit-allowance-gate') ?? false;

/**
 * Gates routing the in-session Ask Heidi bar to a Work project — running
 * in the same side panel Evidence uses — instead of an Evidence chat.
 * Layered on top of Projects itself: this flag only decides which surface
 * the session bar hands off to, so both checks run unconditionally to
 * keep hook order stable regardless of which is false.
 */
export const useIsWorkInSessionEnabled = () => {
  const isProjectsEnabled = useIsProjectsEnabled();
  const isWorkInSessionFlagEnabled = useBooleanFlagValue(
    'release-scribe-work-in-session',
    false
  );
  return isProjectsEnabled && isWorkInSessionFlagEnabled;
};

export const DISABLE_SESSION_ASK_HEIDI_BAR_FLAG_KEY =
  'op-scribe-disable-session-ask-heidi-bar';

/**
 * Kill switch for the session-page Ask Heidi bar (desktop floating bar on V1
 * and V2, plus the mobile Ask Heidi button and modal). TRUE hides it. PostHog,
 * default OFF; country/plan targeting lives in PostHog alongside
 * `release-clinical-coding` (EU-27 geoip × free/pro → 100%) for medical-device
 * compliance.
 *
 * Unlike the other `op-*-disable-*` gates this one treats an unresolved flag
 * (`undefined`: flags not yet loaded, or PostHog blocked) as DISABLED, so the
 * bar can never flash for an excluded cohort before targeting resolves. The
 * bar shows only on an explicit `false`. On-prem is unaffected: the compat
 * layer resolves the bundle to a real boolean, and an absent key is `false`.
 */
export const useIsSessionAskHeidiBarDisabled = () => {
  return useFeatureFlagEnabled(DISABLE_SESSION_ASK_HEIDI_BAR_FLAG_KEY) !== false;
};

/**
 * Floating Ask Heidi bar: recentre the bar into the content area during
 * transcription (note/document-like tabs), keeping it idle until the user
 * clicks into it. Bottom-docked everywhere else. PostHog, default OFF.
 */
export const useIsAskHeidiCentreTranscriptionEnabled = () => {
  return useFeatureFlagEnabled('release-ask-heidi-centre-transcription') ?? false;
};

/**
 * Gates the "new chat" CTA dispatching to the v2 (harness-backed)
 * agent-chat creation path instead of the legacy v1 Evidence chat
 * creation path.
 *
 * Existing chats are unaffected by this flag — their version is
 * baked in at creation time and the chat list dispatch reads it from
 * each row, not from a runtime flag. See
 * ``src/features/projects/chatRouting.ts``.
 *
 * Not Tauri-gated like ``useIsProjectsEnabled``: the v2 path itself
 * works in the browser bundle when ``tool_profile === 'evidence_only'``
 * (no desktop client required). Surfaces that mount the
 * ``clinician_desktop`` profile still need to check Tauri separately.
 *
 * Gated on ``useIsRpaAgentEnabled`` so the v2 create path only ramps
 * where the harness-backed agent is live.
 */
export const useIsAgentChatV2Enabled = () => {
  useBooleanFlagValue('release-scribe-agent-chat-v2', false);
  return useIsRpaAgentEnabled();
};

/**
 * When enabled, shows a personalised source recommendation banner above the chat input
 * suggesting evidence sources based on the user's country and specialty.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 */
/**
 * Kill switch for the source recommendations endpoint.
 * When enabled, all source recommendation queries are disabled.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, along with
 * the two banner getters below. All three resolve through PostHog, which
 * `@heidi/runtime/feature-flags` carries no surface for, so moving one would
 * change WHICH PROVIDER answers (`monorepo-wave-1-p3-feature-flags` Z4). The
 * banner getter's own body says why the flag is there rather than in Flagsmith,
 * in a commented-out `useBooleanFlagValue` call kept beside the live read. They
 * move when the flags are steered to Flagsmith, not before.
 */
export const useIsSourceRecommendationsKillSwitchEnabled = () => {
  return useFeatureFlagEnabled('kill-evidence-source-recommendations') ?? false;
};

export const useIsSourceRecommendationBannerEnabled = () => {
  // Flagsmith targeting currently lacks the necessary cohorts for this experiment.
  // return useBooleanFlagValue(
  //   'release-evidence-enable-source-recommendation-banner',
  //   false
  // );
  return (
    useFeatureFlagEnabled('release-evidence-enable-source-recommendation-banner') ?? false
  );
};

/**
 * When enabled, shows the source recommendation banner inside the sources panel popover.
 */
export const useIsSourceRecommendationPopoverBannerEnabled = () => {
  return (
    useFeatureFlagEnabled(
      'release-evidence-enable-source-recommendation-popover-banner'
    ) ?? false
  );
};

/**
 * MUL-680: New session list with table view and consolidated session lists.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out. The KEY moved
 * with the getter and is still exported here — five production modules import it
 * by name to stamp `experiment:` onto their tracking payloads — so the package
 * DERIVES it from its descriptor (`newSessionListFlag.key`) and this line
 * re-exports it. Re-declaring the literal here would be a SECOND MINTING SITE,
 * which is the one thing ruling 36 exists to refuse and which only
 * `scripts/check-flag-getter-doors.test.ts` can see.
 */
export { NEW_SESSION_LIST_FLAG_KEY } from '@heidi/session/flags';

export { useIsStableSessionSplitStartupEnabled };

/**
 * The new session list, WITH its last online decision replayed while the flag
 * provider is unreachable.
 *
 * The package getter is the live read and stays the source of truth whenever
 * anyone can answer; this body only adds the offline replay, which is app-side
 * by necessity — the cache is a localStorage/keychain pair and
 * `@heidi/session/flags` is a headless door that may hold neither.
 *
 * Offline, the live read collapses to its `false` default, which took
 * the session list back to the legacy layout AND (because
 * `useIsSessionPageV2Enabled` ANDs this) dropped the session detail page to V1
 * mid-consult. Composed HERE rather than at the two V2 gates so the list, the
 * routing and the detail layout cannot disagree about which experience the user
 * is in.
 *
 * `useBuildSessionPath` inside `@heidi/session` still reads the live value, so
 * while offline it builds the LEGACY `/scribe/session/:id` path. That is a
 * redirect, not a broken route: `src/pages/scribe/session/[...id].tsx` reads
 * this getter and replaces itself with `/scribe/sessions/:id`. Moving the
 * replay behind the door would mean handing the package a localStorage port,
 * which is a bigger change than a release fix should carry.
 */
export const useIsNewSessionListEnabled = () => {
  const fromFlagsmith = useIsNewSessionListFlagEnabled();
  const fromOfflineCache = useCachedFlagFallback(NEW_SESSION_LIST_FLAG_KEY);
  return fromFlagsmith || fromOfflineCache;
};

export { useIsMultiScopeStatusCountsEnabled } from '@heidi/session/flags';

/**
 * Ribbon Sidebar gate for React render sites.
 *
 * Returns `true` when either the explicit `release-sidebar-redesign` flag is
 * on OR the new session list is enabled (the new list layout requires the
 * ribbon sidebar).
 */
export const useIsRibbonSidebarEnabled = () => {
  const isSidebarRedesignEnabled =
    useFeatureFlagEnabled('release-sidebar-redesign') ?? false;
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  return isSidebarRedesignEnabled || isNewSessionListEnabled;
};

/**
 * Agent Home v1: activity-led Home and matching primary/secondary nav.
 * PostHog `release-scribe-enable-nav-v3` ANDs the Heidi Work umbrella
 * (`useIsProjectsEnabled`) so a 100% nav-v3 rollout cannot unlock Work.
 * Both reads run unconditionally to keep hook order stable.
 *
 * Keep the established PostHog key while its configured cohorts are active;
 * its dashboard display name can be updated without silently disabling those
 * assignments in preview or production.
 */
export { AGENT_HOME_V1_FLAG_KEY };

export const useIsAgentHomeV1Enabled = () => {
  const isNavV3Enabled = useFeatureFlagEnabled(AGENT_HOME_V1_FLAG_KEY) ?? false;
  const isProjectsEnabled = useIsProjectsEnabled();
  return isNavV3Enabled && isProjectsEnabled;
};

/**
 * `/scribe` login/signup landing on Home. PostHog `release-redirect-to-home`
 * ANDs Agent Home (`useIsAgentHomeV1Enabled` = nav-v3 ∧ Heidi Work) so the hop
 * only fires when Home actually renders as Work — not the Evidence entitlement
 * wall / Evidence home that `/scribe/chat/projects` shows when Work is off.
 *
 * Experiment: `release-redirect-to-home` is the 0–100 assignment vehicle.
 * Exposure is per `/scribe` mount, not per landing decision: this getter runs
 * before the effect knows whether Home is reachable, so `$feature_flag_called`
 * also counts boots that Evidence, support, GPCCMP, or a rescue pre-empt.
 * Dilution is symmetric across arms. Analyse in PostHog (not Amplitude).
 * No `useManualExposure`.
 */
export const REDIRECT_TO_HOME_FLAG_KEY = 'release-redirect-to-home';

export const useIsRedirectToHomeEnabled = () => {
  const isRedirectToHomeEnabled =
    useFeatureFlagEnabled(REDIRECT_TO_HOME_FLAG_KEY) ?? false;
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  return isRedirectToHomeEnabled && isAgentHomeV1Enabled;
};

/**
 * Flagsmith `release-scribe-enable-default-landing-page` (MUL-3717): the
 * per-user, per-workspace "Default landing page" preference (Home or Scribe).
 * ANDs Agent Home (`useIsAgentHomeV1Enabled` = nav-v3 ∧ Heidi Work), and the
 * Heidi Work conjunct is deliberate: nav-v3 without Work renders the Evidence
 * home (or its entitlement wall) at `/scribe/chat/projects`, and the MUL-3718
 * boot resolver only honours the choice under the same gate, so offering the
 * row there would let a user pick a Home that login never sends them to. A
 * 100% Flagsmith rollout can therefore never surface the row on the old nav.
 * Both reads run unconditionally to keep hook order stable.
 *
 * Gates the Settings → Display controls row and the "Make Scribe my landing
 * page" tooltip CTA (DEV-13207). Rollout gate plus kill switch, not an
 * experiment — no `useManualExposure`. Fails closed to OFF.
 */
export const DEFAULT_LANDING_PAGE_SETTING_FLAG_KEY =
  'release-scribe-enable-default-landing-page';

export const useIsDefaultLandingPageSettingEnabled = () => {
  const isFlagEnabled = useBooleanFlagValue(DEFAULT_LANDING_PAGE_SETTING_FLAG_KEY, false);
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  return isFlagEnabled && isAgentHomeV1Enabled;
};

/**
 * Flagsmith: gates the agent-memory management UI — the "Memory" tab in the
 * Work home (`/scribe/chat/projects`) that lists, edits, and retires the
 * user's agent memories. Independent of the backend memory functionality
 * (`release-scribe-enable-memory`) so the UI can be toggled while iterating.
 * When OFF (default) the Memory tab is hidden.
 */
export const useIsAgentMemoryManagementUiEnabled = () => {
  return useBooleanFlagValue('release-scribe-enable-agent-memory-management-ui', false);
};

/**
 * When enabled, prefetches the session-by-id query on session-row click so the
 * GET overlaps the route transition instead of firing after the page mounts.
 * The value is also attached to `perf.scribe.session_open` tracking so the
 * open-latency arms can be compared in Datadog/RUM.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out — the key is
 * DERIVED from its descriptor in the package and re-exported here beside its
 * getter, never re-declared.
 */
export {
  SESSION_PREFETCH_FLAG_KEY,
  useIsSessionPrefetchEnabled,
} from '@heidi/session/flags';

/**
 * APP-5219 / APP-8724 / HAR-999: Gates ONLY the Session Merge setting surface in
 * Settings and the one-time enable-merge onboarding prompt. All other session
 * merge behaviour is ungated — do not reintroduce this flag elsewhere.
 * Platform-split flags (`release-apps-session-merge-web` /
 * `release-apps-session-merge-desktop`) so the two ramps are fully decoupled.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out — both
 * descriptors and the `isTauri()` pick live together in
 * `packages/session/src/flags.ts`, with `isTauri` taken from
 * `@heidi/runtime/platform`.
 */
export { useIsSessionMergeEnabled } from '@heidi/session/flags';

/**
 * APP-7537: Gates web Heidi Remote sync/async functionality.
 */
export const useIsReleaseRemoteSyncAsyncWebEnabled = () => {
  return useBooleanFlagValue('release-apps-remote-sync-async-web', false);
};

/**
 * HAR-1808: Desktop chunked bulk SUCCESS = durable chunk upload only (not
 * transcript/note text). Pushed FE→Tauri; Rust defaults false until set.
 * Flagsmith default OFF — OFF path stays byte-stable (HAR-1751 / HAR-1199).
 */
export const useIsReleaseChronicleBulkSuccessOnChunkUploadDesktopEnabled = () => {
  return useBooleanFlagValue(
    'release-chronicle-bulk-success-on-chunk-upload-desktop',
    false
  );
};

/**
 * Kill switch for the fixed 10 s desktop Remote reconnect cadence. Defaults
 * ON so a flag-provider outage cannot revert the fix; pushed FE→Tauri.
 */
export const useIsReleaseDesktopBleFixedReconnectCadenceMacEnabled = () => {
  return useBooleanFlagValue('release-desktop-ble-fixed-reconnect-cadence-mac', true);
};

/**
 * Kill switch for the Windows no-hold policy on Remote link drops. Its own
 * flag so a Windows revert never touches macOS; defaults ON, pushed FE→Tauri.
 */
export const useIsReleaseDesktopBleFixedReconnectCadenceWindowsEnabled = () => {
  return useBooleanFlagValue('release-desktop-ble-fixed-reconnect-cadence-windows', true);
};

/**
 * HAR-1635: routes Desktop Heidi Remote creates through the server's
 * recording-identity create-or-get endpoint. Rollout gate, not an experiment.
 */
export const useIsRemoteRecordingDedupDesktopEnabled = () => {
  return useBooleanFlagValue(
    'release-apps-use-sessions-from-remote-endpoint-desktop',
    false
  );
};

/**
 * HAR-1254: kill switch for online-session dirty-stop recovery. When ON, a
 * Remote stop whose upload pipeline is dirty (the server did not get every
 * chunk) keeps the on-device recording and auto-recovers via bulk sync instead
 * of deleting it. Default OFF ships dark; the Flagsmith flag is created in a
 * separate infra PR.
 */
export const useIsReleaseRemoteDirtyStopRecoveryEnabled = () => {
  return useBooleanFlagValue('release-apps-remote-dirty-stop-recovery', false);
};

/**
 * APP-6172 (flag created in a separate PR — APP-6513): Heidi Remote new-session
 * logic. When ON, pressing the Remote on a finished session starts a NEW session
 * (iOS/Android parity) instead of resuming it, and auto-opens the recorded
 * session. When OFF (default), the legacy behaviour applies — the Remote
 * resumes/appends to the finished session being viewed, no auto-navigation.
 * Acts as a kill switch.
 */
export const useIsRemoteNewSessionLogicEnabled = () => {
  return useBooleanFlagValue('release-desktop-remote-new-session-ux-logic', false);
};

/** Shipped 100% — PostHog `release-pinned-template-auto-generation`. */
export const useIsTemplatePinningEnabled = () => true;

/**
 * Gates the one-time announce spotlight on the session share button
 * introducing direct session sharing to active users.
 */
export const useIsShareSessionAnnounceSpotlightEnabled = () => {
  return useBooleanFlagValue('release-scribe-session-share-announce-spotlight', false);
};

/**
 * Gated on the reactive parent hook (which transitively requires the
 * auto-gen engine) so queue UI never appears without working pin controls.
 */
export const useIsPinnedTemplateNotegenUiEnabled = () => {
  const isTemplatePinningEnabled = useIsTemplatePinningEnabled();
  const isNotegenUiEnabled = useBooleanFlagValue(
    'release-scribe-enable-pinned-template-notegen-ui',
    false
  );
  return isTemplatePinningEnabled && isNotegenUiEnabled;
};

/**
 * EVI-1339: One-click "Move to Team" promotion of a personal collection.
 * Kill switch for the new affordance shown in `CollectionCard` (dropdown)
 * and `CollectionDetailPage` (header action). When disabled, the existing
 * role/plan gates still apply but the UI entry-points are hidden, which
 * lets us turn the feature off without a code deploy if the cascade
 * misbehaves in production.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsCollectionPromoteToTeamEnabled } from '@heidi/evidence/flags';

/**
 * GRO-2767: When enabled, shows a banner after note generation suggesting
 * an Evidence summary based on clinical signal scoring of the note content.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable:
 * `@heidi/runtime/feature-flags` carries no PostHog surface and moving it would
 * change WHICH PROVIDER answers (`monorepo-wave-1-p3-feature-flags` Z4). The
 * commented-out `useBooleanFlagValue` call below is the Flagsmith read this
 * would become once the cohorts exist.
 */
export const useIsEvidenceSummarySuggestionEnabled = () => {
  // Flagsmith targeting currently lacks the necessary cohorts for this experiment.
  // return useBooleanFlagValue(
  //   'release-evidence-enable-singular-summary-suggestion',
  //   false
  // );
  return (
    useFeatureFlagEnabled('release-evidence-enable-singular-summary-suggestion') ?? false
  );
};

/**
 * When enabled, shows an evidence prompts bar in the Scribe session surface
 * with personalised clinical suggestions and source avatars.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable;
 * same classification as `useIsEvidenceSummarySuggestionEnabled` above, with the
 * same commented-out Flagsmith read below it.
 */
export const useIsEvidencePromptsBarEnabled = () => {
  // Flagsmith targeting currently lacks the necessary cohorts for this experiment.
  // return useBooleanFlagValue('release-evidence-enable-clinical-suggestions-bar', false);
  return (
    useFeatureFlagEnabled('release-evidence-enable-clinical-suggestions-bar') ?? false
  );
};

/**
 * Floating / side-panel Ask Heidi: personalised Sources button (avatar stack,
 * sand/selected fill, tooltip). Same PostHog key as the guided "/" menu
 * follow-up (#17099) — this PR only ships the Sources face.
 *
 * Split from `release-sources-button-states` so this cluster ships independently
 * of centred-bar placement. Rollout/kill gate — useManualExposure is attached
 * at the floating-input call site where the variant is visible.
 */
export const useIsAskHeidiSourcesShortcutsEnabled = () => {
  return useFeatureFlagEnabled('release-ask-heidi-sources-shortcuts') ?? false;
};

/**
 * GRO-2846: When enabled, displays the Evidence search bar in a central location
 * within the empty note area during transcription, with session-stage-based
 * use case chips (before/during/after).
 *
 * Off whenever Work in session is on: the bar hands its prompt to the Evidence
 * chat through `pendingChatMessageAtom`, which a Work side panel never reads.
 * Both reads run unconditionally to keep hook order stable.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable;
 * same classification as the two getters above.
 */
export const useIsEvidenceCentralSearchBarEnabled = () => {
  const isWorkInSessionEnabled = useIsWorkInSessionEnabled();
  const isExperimentEnabled =
    useFeatureFlagEnabled('experiment-evidence-central-search-bar') ?? false;
  return isExperimentEnabled && !isWorkInSessionEnabled;
};

/**
 * Inline images in Evidence references and in the projects evidence timeline.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsEvidenceInlineImagesEnabled } from '@heidi/evidence/flags';

/**
 * Enables the V2 signup-origin marker for a targeted cohort.
 *
 * This does not override the multivariate `release-new-onboarding-flow`
 * assignment; it only allows the targeted cohort to satisfy the localStorage
 * eligibility check after signup.
 */
export const useIsForceV2OnboardingLocalStorageOnEnabled = () => {
  return useFeatureFlagEnabled('release-force-v2-onboarding-localstorage-on') ?? false;
};

/**
 * Gates features that depend on `global_usage` data being populated and
 * trustworthy: the "Recommended for {specialty}" relabel on the New User — Solo
 * picker variant, and the Popularity sort option in the template filter.
 * Both features are hidden until this is flipped on.
 */
export const useIsGlobalUsageReady = () => {
  return useBooleanFlagValue('release-scribe-global-usage-ready', false);
};

/**
 * The onboarding guided tour: the single note-edit accordion is promoted to
 * three dedicated screens (detail / artefacts / evidence) and the team-invite
 * step moves ahead of the transcription step.
 *
 * The `experimental-3` arm of `release-new-onboarding-flow` — the one flag that
 * picks the onboarding flow (see `GET_ONBOARDING_V2_VARIANT`). It replaced the
 * nested `exp-growth-onboarding-guided-tour` boolean so a user's flow is a single
 * sticky PostHog assignment rather than two flags ANDed together. The tour is an
 * expansion of the sample-conversation flow, so `usePersonalizationForm` still
 * requires a preview-capable viewport before it renders the tour screens.
 * Exposure is fired manually at the mic-test entry point
 * (`useGuidedTourExposure`) because analysis happens in Amplitude.
 */
export const useIsOnboardingGuidedTourEnabled = () => {
  return useFeatureFlagVariantKey(ONBOARDING_FLOW_FLAG_KEY) === 'experimental-3';
};

/**
 * When ON, hides the Evidence sidebar *nav entry* while the app is running inside
 * an EHR Launch (SMART on FHIR — Cerner, Epic, etc.). OFF (default) keeps the
 * Evidence nav entry visible as before, even in an EHR Launch.
 *
 * NOTE: this currently gates only the sidebar nav entry — NOT the full Evidence
 * surface. The panel can still be opened via other paths (auto-open on
 * transcription, floating Ask Heidi, clinical-task CTAs, deep links to
 * `/scribe/chat` etc.). Full-surface suppression would be threaded through
 * `useEvidenceAccess` (see the Shadow Heidi `AccessTier.NOACCESS` precedent).
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`. Two domains touch this flag
 * and only one may declare it: the key is integrations-namespaced and the
 * condition is "inside an EHR Launch", which is that domain's surface, so the
 * Evidence nav item reads it through this re-export — a legal cross-root read,
 * and the arrangement ruling 36 (b) prescribes for a foreign domain's flag.
 */
export { useIsEvidenceSidePanelDisabledInEhrLaunch } from '@heidi/integrations/flags';

/**
 * Kill switch for onboarding resume-after-refresh (GRO-3866) — PostHog
 * `op-growth-disable-onboarding-resume`, boolean, default OFF (resume on).
 *
 * Deliberately a DISABLE flag rather than a release gate. Resuming is the fixed
 * behaviour and the flag exists only to switch it back off without a deploy, so
 * an unresolved flag (PostHog still loading, or blocked by an extension) must
 * fail open. It also has to: the wizard restores once at mount, so a flag that
 * arrives after first render could never retroactively enable a resume, and a
 * fail-closed default would silently reinstate the bug for anyone PostHog can't
 * reach.
 *
 * Flipping it ON stops both the restore and the writes, and clears any record
 * already in `sessionStorage`, from the next page load.
 */
export const useIsOnboardingResumeEnabled = () => {
  return !(useFeatureFlagEnabled('op-growth-disable-onboarding-resume') ?? false);
};

/**
 * Gates the onboarding compliance/safety copy: the "Intended use statement"
 * accordion + sticky CTA on the privacy step and the documentation reminder on
 * the note-ready step. Defaults ON.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out.
 */
export { useIsOnboardingComplianceCopyEnabled } from '@heidi/growth/flags';

export const useIsDesktopDictationFlagEnabled = () => {
  return useFeatureFlagEnabled(DESKTOP_DICTATION_FLAG) ?? false;
};

/**
 * SCR-4586: rollout gate for routing dictation into remote-desktop / VDI clients
 * (Citrix Viewer) via synthetic keystrokes. ANDed with the platform dictation
 * rollout per the dictation sub-flag convention. Synced to Rust by
 * `DictationProvider`.
 *
 * Flagsmith/OpenFeature (managed via Terraform in `oscerai/infra`), matching the
 * other `release-scribe-*` sub-flags. Defaults **off** — ships dark for a
 * controlled rollout; enable in Flagsmith once the keystroke path is smoke-tested
 * on a real native Citrix session.
 */
export const useIsDictateRemoteDesktopKeystrokeEnabled = () => {
  const enabled = useBooleanFlagValue(
    'release-scribe-dictate-remote-desktop-keystroke',
    false
  );
  return enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Dictaphone (RecMic / PowerMic / SpeechMike) gate: on when EITHER device
 * rollout phase is on — phase 1 (`release-scribe-dictate-device-detected-popup`)
 * or phase 2 (`release-scribe-dictate-device-setup`). Starts the Rust HID
 * reader so a Record press engages dictation, and arms the connect-time
 * awareness card. Desktop / Tauri only — ANDed with isTauri() and the platform
 * dictation parent gate. Synced to Rust by DictationProvider.
 */
export const useIsDictateHardwareMicEnabled = () => {
  const detectedPopupEnabled = useIsDictateDeviceDetectedPopupEnabled();
  const deviceSetupEnabled = useIsDictateDeviceSetupEnabled();
  return (
    isTauri() &&
    (detectedPopupEnabled || deviceSetupEnabled) &&
    IS_PLATFORM_DICTATION_ENABLED()
  );
};

/**
 * SCR-7131: show the native dictation widget on the display containing the
 * cursor (vs always the primary display). Flagsmith/OpenFeature rollout /
 * kill switch — not an experiment, no manual exposure. ANDed with the
 * platform dictation rollout. Synced to Rust by `DictationProvider`.
 * Defaults **off** (primary-monitor placement).
 */
export const useIsDictateWidgetCursorDisplayEnabled = () => {
  const enabled = useBooleanFlagValue(
    'release-scribe-dictate-widget-cursor-display',
    false
  );
  return enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * SCR-7481: wider Windows text-field detection for Dictate Anywhere — the
 * whole `Chrome_WidgetWin_<N>` class family and CEF hosts route to the paste
 * path, the UIA probe accepts `TextPattern` / `Edit` / `Document` evidence
 * alongside an active caret, and capture treats a foreground main window as
 * focused (WebView2 steals tao's focus flag). Flagsmith/OpenFeature rollout /
 * kill switch — not an experiment, no manual exposure. ANDed with the platform
 * dictation rollout (`release-desktop-dictation` on desktop). Synced to Rust
 * by `DictationProvider`. Defaults **off** (pre-SCR-7481 detection).
 */
export const useIsDictateBrowserFieldDetectionEnabled = () => {
  const enabled = useBooleanFlagValue('release-dictation-browser-field-detection', false);
  return enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * SCR-6773: desktop Dictate may use a machine-local mic preference independent
 * of the session recording mic. Flagsmith/OpenFeature rollout / kill-switch
 * (not an Amplitude experiment — no manual exposure). Defaults **off**.
 *
 * Desktop/Tauri only and ANDed with the platform dictation parent gate so web
 * never sees the decoupled path.
 */
export const DICTATE_SEPARATE_MICROPHONE_FLAG_KEY =
  'release-scribe-dictate-separate-microphone';

export const useIsDictateSeparateMicrophoneEnabled = () => {
  const enabled = useBooleanFlagValue(DICTATE_SEPARATE_MICROPHONE_FLAG_KEY, false);
  return isTauri() && enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/** Non-hook companion for Rematch effects / imperative paths. */
export const isDictateSeparateMicrophoneEnabled = () => {
  if (!isTauri() || !IS_PLATFORM_DICTATION_ENABLED()) return false;
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    DICTATE_SEPARATE_MICROPHONE_FLAG_KEY,
    false
  );
};

export const useIsWebDictationFlagEnabled = () => {
  return useFeatureFlagEnabled(WEB_DICTATION_FLAG) ?? false;
};

export const useIsWebDictationOnboardingFlagEnabled = () => {
  return useBooleanFlagValue('release-web-dictation-onboarding', false);
};

/**
 * SCR-7070: Flagsmith kill switch for the Dictate tour banner and the
 * CTA-opened tour. Defaults **off** for a controlled rollout. Raw flag value —
 * parent `release-desktop-dictation` stays a separate AND at the call site,
 * matching web dictation onboarding.
 *
 * MOVED to `@heidi/dictate` by ruling 36: the key/default pair is declared
 * and read once in `packages/dictate/src/flags.ts`. This line is the
 * re-export that keeps the barrel's importers — and their
 * `jest.mock('@/features/flags')` literals — resolving unchanged.
 */
export {
  isDictateUserFeedbackEnabled,
  isDictationCustomVocabularyEnabled,
  useIsDictateReengagementCardEnabled,
  useIsDictateTourBannerEnabled,
  useIsDictationCustomVocabularyEnabled,
} from '@heidi/dictate/flags';

// Published from the local binding, like `useIsNewSessionListEnabled`.
export { useIsDictateDeviceDetectedPopupEnabled, useIsDictateDeviceSetupEnabled };

/**
 * Settings Dictaphone row. That row is the entry to the add-device dialog,
 * so the dialog itself is not gated here. Defaults **off**; not synced to Rust.
 */
export const useIsDictateDeviceSetupVisible = () => {
  const enabled = useIsDictateDeviceSetupEnabled();
  return isTauri() && enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Post-dictation output feedback is subordinate to the current platform's
 * dictation rollout, as well as its own Flagsmith kill switch.
 */
export const useIsDictateUserFeedbackEnabled = (): boolean => {
  const isUserFeedbackEnabled = useIsDictateUserFeedbackFlagEnabled();
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  return isUserFeedbackEnabled && isPlatformDictationEnabled;
};

/**
 * The milestone-one snippets importer is subordinate to the platform Dictate
 * rollout as well as its own release flag.
 */
export const useIsDictationImportSnippetsEnabled = (): boolean => {
  const isImportEnabled = useIsDictationImportSnippetsFlagEnabled();
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  return isImportEnabled && isPlatformDictationEnabled;
};

const SECTIONS_IN_NOTES_FLAG_KEY = 'release-scribe-enable-sections-in-notes';

export const useIsSectionsInNotesEnabled = () => {
  return useBooleanFlagValue(SECTIONS_IN_NOTES_FLAG_KEY, false);
};

/**
 * The regional servers' kill switch for honouring a team invite answer during
 * the onboarding gate's signup call. Read here as well: the V3 invite screen
 * on `/join-team` is the way into that flow, so it opens only while the
 * answer it collects will be acted on. The page is pre-login, so the
 * environment default is what is read, not a per-user override.
 */
const ONBOARD_TEAM_INVITE_FLAG_KEY = 'release-onboard-team-invite';

/**
 * Whether a signed-out person opening a team invite link sees the V3 invite
 * screen on `/join-team` (the hosted onboarding page's own screen, in its
 * layout) in place of the legacy account modal. Off restores the modal
 * without a deploy.
 */
export const useIsV3InviteLinkScreenEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(ONBOARD_TEAM_INVITE_FLAG_KEY, false);
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Whether problem-based charting is enabled for the current user.
 *
 * Prefer this over the imperative `isProblemBasedChartingEnabled` anywhere a
 * hook can run: it subscribes to OpenFeature, so dependent memoised values
 * (tiptap extension arrays, push-note eligibility) recompute when the flag
 * resolves after mount instead of being stuck on the pre-hydration default.
 *
 * Reads its key/default from `problem-based-charting-flag` so the pair is
 * declared once for both call styles.
 */
export const useIsProblemBasedChartingEnabled = () => {
  return useBooleanFlagValue(
    PROBLEM_BASED_CHARTING_FLAG_KEY,
    PROBLEM_BASED_CHARTING_FLAG_DEFAULT
  );
};

/**
 * Low-latency note editing for the markdown block editor.
 *
 * When enabled, the editor emits partial document updates via a lightweight
 * diff algorithm instead of serialising the whole note on every keystroke,
 * cutting perceived input lag on large documents.
 */
export const useIsLowLatencyNoteEditingEnabled = () => {
  return useBooleanFlagValue('release-scribe-low-latency-note-editing', false);
};

/**
 * Hook form of `getRegionalUpdaterGate` (APP-6113 §5.18/§5.19) for React
 * components. Subscribes to OpenFeature provider status so the gate flips from
 * `'unknown'` → `'on'`/`'off'` (and the component re-renders) once the provider
 * resolves after mount — closing the startup race where a not-yet-ready flag
 * read as `false` and ran the global updater. The rematch model effect
 * (`tauriUpdateModel.ts`) can't use hooks, so it uses the static
 * `getRegionalUpdaterGate`; both read the same flag. See that function for the
 * global-kill-switch operational constraint.
 */
export const useRegionalUpdaterGate = (): RegionalUpdaterGate => {
  const status = useOpenFeatureClientStatus();
  const enabled = useBooleanFlagValue(REGIONAL_UPDATER_FLAG_KEY, false);
  if (status !== ProviderStatus.READY) return 'unknown';
  return enabled ? 'on' : 'off';
};

/**
 * ARC-402: Gates the Tiptap code-block feature (extension, slash entry,
 * text-menu button, and fenced markdown round-trip). Default OFF; flipped
 * per-environment in Flagsmith.
 */
export const useIsScribeBlockEditorCodeblockEnabled = () => {
  return useBooleanFlagValue('release-scribe-block-editor-codeblock', false);
};

/**
 * Non-hook companion for `useIsScribeBlockEditorCodeblockEnabled`. Used by
 * plain-JS call sites: the slash-command group factory and the markdown
 * helpers (`simpleMarkdownToHtml` / `simpleHtmlToMarkdown`), which are not
 * React components.
 */
export const isScribeBlockEditorCodeblockEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-block-editor-codeblock',
    false
  );
};

/**
 * When on, `getDisplayMedia` sets `selfBrowserSurface: 'include'` so the current
 * tab is selectable in the telehealth tab-audio picker. Chrome hides it by
 * default (`selfBrowserSurface: 'exclude'`). OFF preserves that default.
 * Static (non-hook) form — read from the audio-graph store, outside React.
 */
export const IS_TELEHEALTH_ALLOW_CURRENT_TAB_ENABLED = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-telehealth-allow-current-tab',
    false
  );
};

/**
 * Get the sections-in-notes flag outside of React (e.g. in helpers, utils).
 * Use the hook `useIsSectionsInNotesEnabled` inside components.
 */
export const isSectionsInNotesEnabled = () => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  return client.getBooleanValue(SECTIONS_IN_NOTES_FLAG_KEY, false);
};

/**
 * Killswitch for the emphasis whitespace-expulsion fix in
 * `simpleHtmlToMarkdown` (edge whitespace moved outside the `**`/`_`
 * delimiters so saved markdown re-parses under CommonMark flanking rules).
 * OFF (default): the fix is active. ON: break-glass — the legacy
 * in-delimiter output is written instead, byte-identical to the pre-fix
 * pipeline. Called outside React (markdown helpers), so non-hook only.
 */
export const isEmphasisWhitespaceExpulsionKilled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'op-scribe-disable-emphasis-whitespace-expulsion',
    false
  );
};

/**
 * Gates task assignment feature.
 * When enabled, allows assigning tasks to team members.
 *
 * MOVED to `@heidi/tasks` by ruling 36's fan-out batch 1 — declared
 * and read once in `packages/tasks/src/flags.ts`. Thirteen production consumers,
 * the widest-consumed name in that batch, and every one of them keeps importing
 * it from here.
 */
export { useIsTaskEnableAssignmentEnabled } from '@heidi/tasks/flags';

/**
 * MUL-2413: Gates team assignment in the clinical task assignee selector.
 * When enabled, org teams appear as selectable assignees alongside individual members.
 *
 * MOVED to `@heidi/tasks` by ruling 36's fan-out batch 1. No production consumer
 * at conversion — a
 * candidate for retirement, but retiring a flag is a
 * `docs/feature-flag-cleanups.md` change and a different decision from where the
 * declaration lives, so it moved rather than being deleted.
 */
export { useIsTasksEnableAssignmentFgaGenericGrantEnabled } from '@heidi/tasks/flags';

/**
 * MUL-3055: Gates team list visibility in the clinical task assignee selector.
 * When enabled, org teams appear as selectable assignees alongside individual members.
 *
 * MOVED to `@heidi/tasks` by ruling 36's fan-out batch 1 — declared and read once in
 * `packages/tasks/src/flags.ts`.
 */
export { useIsTaskEnableTeamAssignmentEnabled } from '@heidi/tasks/flags';

/**
 * Gates the copy-to-clipboard controls in the call summary modal:
 * - Copy summary button on the summary panel
 * - Copy-all transcript button next to the Transcript header
 * - Per-message copy button revealed on hover in transcript bubbles
 *
 * All three controls ship together behind this single flag so the
 * "copy" feature can be rolled out / rolled back as one unit.
 *
 * MOVED to `@heidi/calls` by ruling 36's fan-out batch 1 — declared
 * and read once in `packages/calls/src/flags.ts`.
 */
export { useIsCallsLogCopyButtonsEnabled } from '@heidi/calls/flags';

/**
 * Gates the Nango-powered integrations surface (Settings → Integrations →
 * Connected apps, marketplace card, Connect UI). Default OFF until BE
 * `/integrations/nango/*`, a signed HIPAA BAA, and region-allowed Nango
 * hosting (`NEXT_PUBLIC_NANGO_HOST`) are in place — do not enable for
 * AU/EU/UK/CA tenants until residency-compliant hosting is configured.
 *
 * ANDs the Heidi Work umbrella (`useIsProjectsEnabled`): Connected apps only
 * serve Work, so the Flagsmith key is a 0%/100% kill switch rather than the
 * rollout. Both reads run unconditionally to keep hook order stable. Mirrors
 * ml-scribe `release_integrations_enable_nango`.
 *
 * Local-dev escape hatch: `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS=true`
 * or `NEXT_PUBLIC_NANGO_FORCE_ENABLE=true` (OpenFeature is not patched by
 * FORCE_ENABLE_ALL alone for this Flagsmith key).
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, and it is
 * the live instance of the trap beside the forceAll collapse. The door's injected
 * `forceEnableAll` reproduces the FIRST of those two env reads and not the
 * second: `NEXT_PUBLIC_NANGO_FORCE_ENABLE` is a bespoke var, a package reads no
 * env of its own, and collapsing the body onto `useBooleanFlag` would silently
 * drop that escape hatch. It moves when the bespoke var goes, not before.
 */
export const useIsNangoIntegrationsEnabled = () => {
  const flagEnabled = useBooleanFlagValue('release-integrations-enable-nango', false);
  const isHeidiWorkEnabled = useIsProjectsEnabled();
  // Local-dev escape hatch — env check after the hook (Rules of Hooks).
  if (
    process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true' ||
    process.env.NEXT_PUBLIC_NANGO_FORCE_ENABLE === 'true'
  ) {
    return true;
  }
  return flagEnabled && isHeidiWorkEnabled;
};

/**
 * EVI-1473: Gates the SharePoint Library Ingest surface. This hook controls the Library
 * "Sharepoint" scope filter pill (and, on the backend, the
 * `/sharepoint/*` router + the ingestion service entry point so docs
 * actually flow into the Evidence Library).
 *
 * Operator note: enable this flag for tenants that should ingest SharePoint
 * docs into the Evidence Library. Without it, the settings connect page is
 * visible but Library ingest and the BE router stay gated.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`. Integrations-owned by key
 * and by story (a SharePoint connector) even though the surface it gates is an
 * Evidence Library filter pill, which is a cross-root read of the kind this
 * barrel exists to serve.
 */
export { useIsSharepointLibraryIngestEnabled } from '@heidi/integrations/flags';

/**
 * APP-3957: Gates WiFi bulk sync for Chronicle device on Desktop.
 * WiFi transfer is currently unstable due to firmware TCP behaviour — this flag
 * controls visibility of the "Switch to Wi-Fi (Beta)" button in the bulk sync UI.
 */
export const useIsChronicleWifiSyncEnabled = () => {
  const flagEnabled = useBooleanFlagValue(
    'release-apps-enable-chronicle-device-wifi-sync-desktop',
    false
  );
  if (!isTauri()) return false;
  return flagEnabled;
};

/**
 * Gates Heidi Remote (Chronicle device) features on Desktop.
 */
export const useIsDesktopChronicleEnabled = () => {
  const flagEnabled = useBooleanFlagValue(
    'release-apps-enable-chronicle-device-desktop',
    false
  );
  if (!isTauri()) return false;
  return flagEnabled;
};

/**
 * Track your Remote — Soft-PASS 1→open trusted status_url / N→picker
 * (iOS Soft-PASS parity, HAR-1831 / #4288). Paste is not Soft-PASS primary UX.
 * Flagsmith key mirrors iOS `release-ios-track-your-remote`; default OFF.
 * Returns the Flagsmith boolean only — web Soft-PASS is allowed; entry
 * visibility is no longer Tauri-gated. Open path still uses the Tauri URL
 * opener where needed on desktop.
 *
 * Nested dependency: desktop nav into `/devices` is gated by outer
 * `release-apps-enable-chronicle-device-desktop`. This inner flag only gates
 * the Track-your-Remote entry once that surface is reached. Deep-link
 * `/devices` alone is ungated — roll out the outer Chronicle desktop flag
 * first (or keep both OFF). Inner ON × outer OFF is deep-link-only Soft-PASS.
 */
export const useIsDesktopTrackYourRemoteEnabled = () => {
  return useBooleanFlagValue('release-desktop-track-your-remote', false);
};

/**
 * Dev-only Chronicle dashboard gate.
 * Combines: isTauri() + non-PROD env + NEXT_PUBLIC_DEV_DASHBOARD env var + Flagsmith flag.
 */
export const useIsChronicleDevDashboardEnabled = () => {
  const isChronicleEnabled = useIsDesktopChronicleEnabled();
  if (!isTauri()) return false;
  if (getEnvironment() === ENV_NAME.PROD) return false;
  const envGate = process.env.NEXT_PUBLIC_DEV_DASHBOARD === 'true';
  return envGate && isChronicleEnabled;
};

/**
 * APP-7087: gates the Chronicle "select local firmware" sideload action (native
 * .bin picker) on Desktop. Flagsmith-controlled, default OFF; replaces the
 * NEXT_PUBLIC_DEV_DASHBOARD env gate so it can be enabled remotely (incl. prod).
 */
export const useIsChronicleLocalFirmwareUpgradeEnabled = () => {
  const flagEnabled = useBooleanFlagValue(
    'release-chronicle-local-firmware-upgrade-desktop',
    false
  );
  if (!isTauri()) return false;
  return flagEnabled;
};

/**
 * Schedule Upload — PDF support: when enabled, the schedule upload flow
 * (modal + sidebar dropzone) accepts PDFs in addition to CSV/PNG/JPEG.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out.
 */
export { useIsScheduleUploadPdfEnabled } from '@heidi/session/flags';

/**
 * Emergency kill switch for schedule-sidebar drag-and-drop. When enabled, the
 * sidebar restores the propagation stop that prevents react-dropzone from
 * accepting drag events; browse and paste uploads remain available.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out.
 */
export { useIsScheduleSidebarDragDropKilled } from '@heidi/session/flags';

/**
 * Schedule Upload - context field: when enabled, each draft session in the
 * upload preview shows an editable "Session context" textarea that is forwarded
 * to the bulk-create endpoint and stored on `Session.clinician_notes`.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out.
 */
export { useIsScheduleUploadContextFieldEnabled } from '@heidi/session/flags';

/**
 * Note footer cluster — collapse on peek sheet.
 * When enabled, the right-side note footer toggles (Personalisation,
 * Codes, Tasks, Verify) collapse into icon-only buttons whenever the
 * floating Ask Heidi / Evidence peek sheet is rendered above the
 * input, so the toggles stay clickable instead of being occluded.
 * When disabled, the toggles always render with their full labels
 * (legacy behavior).
 */
export const useIsNoteFooterCollapseOnPeekEnabled = () => {
  return useBooleanFlagValue('release-note-footer-collapse-on-peek', false);
};

/**
 * Gates the v2 integration marketplace listing and its settings entry points.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`.
 */
export { useIsIntegrationMarketplaceV2Enabled } from '@heidi/integrations/flags';

/**
 * ANZ-2490: whether marketplace discovery is hidden for the current identity.
 * Declared and read in `@heidi/integrations/flags`; the settings gate composes
 * this with {@link useAppliedFlagTargetingKey} so pre-identify OFF is not painted.
 */
export { useIsIntegrationsMarketplaceHidden } from '@heidi/integrations/flags';

/**
 * EVI-1324: Source-control BE persistence kill switch.
 *
 * Gates `useSyncSourceControlSelections`, which syncs the in-memory
 * `sourceControlToggleAtom` with `User.evidence_settings.source_control`
 * via PATCH /v1/user/. When OFF, no BE reads or writes happen for source
 * control — the atom stays in-memory only (selections reset on reload),
 * and the hydration gate is unblocked immediately so existing source
 * badges/icons still render. Acts as a runtime kill switch we can flip
 * in Flagsmith without a code deploy if the BE sync regresses.
 *
 * Default: `false` until rolled out in Flagsmith.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsEvidenceSourceControlBeSyncEnabled } from '@heidi/evidence/flags';

/**
 * INC-349 / DEV-12867: remote override for the Evidence readiness fail-open
 * deadline (watchdog + send path). A tuning VALUE, not a kill switch — the
 * fail-open net always fires; this only adjusts WHEN, and the caller clamps
 * the value (`getSourceControlFailOpenTimeoutMs` in
 * `useSourceControlReadinessFailOpen`) so no remote setting can neuter the
 * net or hair-trigger it. Read imperatively, not as a hook: the watchdog
 * arms pre-auth, before the OpenFeature provider may have reconciled, and
 * the baked default must win in that window. Exists because the desktop app
 * bakes the frontend into its bundle — retuning a hard-coded constant there
 * would cost a full desktop release cycle. Flag absent in Flagsmith ⇒
 * `defaultMs` ⇒ behavior identical to the baked constant.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, and the classification is SHAPE rather
 * than provider, which makes it the more interesting of this domain's nine
 * stay-behinds. `@heidi/runtime/feature-flags` publishes `readFlag` (boolean) and
 * `readStringFlag` and NO number reader at all, so there is nothing on the door
 * to move this onto. Reaching for `readFlag` and coercing is not the workaround
 * it looks like: it would change the value's type at the one call site that
 * clamps it. It moves when the door publishes a number read.
 */
export const getEvidenceFailOpenTimeoutOverrideMs = (defaultMs: number) => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getNumberValue(
    'release-evidence-fail-open-timeout-ms',
    defaultMs
  );
};

/**
 * EVI-1961 / EVI-2151: When ON, team/org Evidence settings lock the last L1
 * source toggle, auto-seed team libraries on enforce, and backend rejects
 * zero usable sources. Default OFF until rolled out in Flagsmith.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsEvidenceRequireAtLeastOneSourceEnabled } from '@heidi/evidence/flags';

/**
 * EVI-1958: When ON, team library enforcement uses per-collection Enforce /
 * Optional modes instead of the binary library_collection_ids_changeable lock.
 * Default OFF until rolled out in Flagsmith.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsEvidencePerCollectionLibraryModesEnabled } from '@heidi/evidence/flags';

/**
 * Gates agent-driven session recording (the ``ui.session_recording`` event
 * dispatcher, the global recorder overlay, and the artifact-header controls).
 * No dedicated flag: it follows Projects.
 */
export { useIsAgentSessionRecordingEnabled };

/**
 * Dedicated flag for the native desktop-target control loop — the Rust side
 * that registers this desktop as a computer-use target and handles
 * `computer_use.target_assigned` over its own authenticated SSE connection.
 *
 * This is the listener's dedicated FE rollout gate, combined with overall
 * Projects enablement by `useDesktopTargetRuntime`. It controls only native
 * target registration, heartbeat, and the user-level assignment SSE loop;
 * request execution mode is frozen by the backend on each Project.
 */
export const useIsDesktopTargetRuntimeEnabled = () => {
  return useBooleanFlagValue('release-scribe-enable-rpa-desktop-target-runtime', false);
};

export {
  DESKTOP_TARGET_SELECTOR_FLAG_KEY,
  isDesktopTargetSelectorEnabled,
  useIsDesktopTargetSelectorEnabled,
} from './desktop-target-selector-flag';

/** Desktop RPA: `isTauri() && useIsProjectsEnabled()`. See Projects umbrella JSDoc. */
export const useIsRpaAgentEnabled = () => {
  const isProjectsEnabled = useIsProjectsEnabled();
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') {
    return isTauri();
  }
  return isTauri() && isProjectsEnabled;
};

/**
 * Cmd+Shift+H Floaty overlay kill switch (reversible).
 * Requires desktop RPA entitlement plus ``release-scribe-cmd-shift-h-overlay``.
 *
 * Not affected by ``NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS`` for the
 * overlay Flagsmith key — that env only short-circuits
 * {@link useIsRpaAgentEnabled}'s Projects entitlement. The overlay flag must
 * be set explicitly (Flagsmith / OpenFeature) during dev/staging testing.
 */
export const useIsCmdShiftHOverlayEnabled = () => {
  const isRpaAgentEnabled = useIsRpaAgentEnabled();
  const isOverlayFlagEnabled = useBooleanFlagValue(
    'release-scribe-cmd-shift-h-overlay',
    false
  );
  return isRpaAgentEnabled && isOverlayFlagEnabled;
};

/** RPA workflow recording. Requires RPA agent; Projects can enable without the recording flag. */
export const useIsRpaRecorderEnabled = () => {
  const isRpaAgentEnabled = useIsRpaAgentEnabled();
  const isRecordingEnabled = useBooleanFlagValue(
    'release-scribe-enable-rpa-recording',
    false
  );
  const isProjectsEnabled = useIsProjectsEnabled();
  return isRpaAgentEnabled && (isRecordingEnabled || isProjectsEnabled);
};

/**
 * Auto-Link Sessions: gate for the auto-link-sessions slice — auto-includes
 * recent sessions from the same patient profile as linked-session context, and
 * surfaces the one-time Auto Pull History spotlight inviting users to opt in.
 * Distinct from the older `release-linked-sessions-auto-select` flag in
 * `./legacy-flags`, which has no consumers.
 */
export const useIsAutoLinkSessionsEnabled = () => {
  return useBooleanFlagValue('release-scribe-auto-link-sessions', false);
};

/**
 * Patient-context auto-extract: gates the post-session field-extraction review
 * popup and the Data Management opt-out toggle. The per-user opt-out lives in
 * `session_settings.intends_to_auto_extract_patient_context`; this flag is the
 * rollout knob (Heidi Labs today), to be retired once the setting is the sole
 * control. Mirror of `IS_PATIENT_CONTEXT_AUTO_EXTRACT_ENABLED` in
 * `./legacy-flags` as a hook for React render gating.
 */
export const useIsPatientContextAutoExtractEnabled = () => {
  // Read imperatively (not via useBooleanFlagValue) so this resolves identically
  // to IS_PATIENT_CONTEXT_AUTO_EXTRACT_ENABLED, the gate the post-session flow
  // already uses. The React hook returns its default until the provider finishes
  // reconciling, which can hide the settings toggle on first render even when
  // the flag is on.
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-patient-context-auto-extract',
    false
  );
};

// The Athena sectional editor/JSON-cohort STRING flag hooks (trial-users,
// markdown cutover, JSON window) were REMOVED with the client-side
// classification they served: the editor version is now resolved by the
// backend and consumed via
// `@/hooks/editor-version/useResolvedEditorVersion` (ml-scribe #18956 /
// AME-1002). The Flagsmith flags still exist — the backend reads them inside
// its own determination — but the FE no longer needs value accessors for
// them.

/**
 * AdvancedMD MARKDOWN sectional editor pilot flag. Lives in a `.ts`
 * sibling so the hook + non-hook reader do not trip
 * `react-refresh/only-export-components` in this `.tsx` barrel (re-exports
 * are exempt). See `./amd-markdown-sectional-editor`.
 */
export {
  getIsAmdMarkdownSectionalEditorEnabled,
  useIsAmdMarkdownSectionalEditorEnabled,
} from './amd-markdown-sectional-editor';

/**
 * AME-507: Provider-agnostic dept/group columns on the integration schedule
 * appointment list, driven by `appointment.display_facets` (`show_in_row`)
 * from GET schedule — plus the provider group / department schedule filters,
 * whose key predates the provider-agnostic rework and so still says "athena".
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`.
 *
 * THE KEY MOVED WITH THEM AND IS STILL EXPORTED HERE. It is a barrel export, so
 * it cannot vanish; re-declaring the literal on this line instead would be a
 * SECOND MINTING SITE, which is what ruling 36 refuses and what the door-spanning
 * check in `scripts/check-flag-getter-doors.test.ts` refuses by name. The package
 * derives it from the descriptor — `export const SCHEDULE_FILTER_OPTIONS_FLAG_KEY
 * = scheduleFilterOptionsFlag.key` — and this line re-exports it beside its
 * getter.
 */
export {
  SCHEDULE_FILTER_OPTIONS_FLAG_KEY,
  useIsMultiProviderScheduleEnabled,
  useIsScheduleFilterOptionsEnabled,
} from '@heidi/integrations/flags';

/**
 * The find-or-link patient combobox across the session header and session list.
 *
 * MOVED to `@heidi/patients/flags` — the fourth descriptor + getter on that
 * door, on ruling 36's recipe. The body was a plain
 * `useBooleanFlagValue` read with no PostHog composition, so nothing about WHICH
 * PROVIDER answers changes. ONE DISCLOSED DELTA, a gain rather than a risk:
 * `useBooleanFlag` behind the door honours the injected `forceEnableAll`
 * override that this body did not, from the same
 * `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` read `src/utils/runtime-wiring.ts`
 * already performs.
 */
export { useIsFindOrLinkPatientEnabled } from '@heidi/patients/flags';

const DICTATION_LANGUAGE_SETTINGS_FLAG_KEY = 'release-dictation-language-settings';

/**
 * Gates the dictation language-settings surface.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped. The flag
 * half is a plain Flagsmith read and would move on its own; what holds it is the
 * composition. It ANDs `useIsPlatformDictationEnabled()`, which resolves through
 * PostHog, and a composition inherits its operand's mechanism —
 * `@heidi/runtime/feature-flags` carries no PostHog surface, so rewriting the
 * pair onto the door would change WHICH PROVIDER answers for the platform gate.
 * This is verbatim the case `packages/dictate/src/flags.ts` records as the
 * reason its own siblings stayed.
 *
 * Same classification for `useIsDictationOutputLanguageFlagEnabled` below.
 */
export const useIsDictationLanguageSettingsFlagEnabled = () => {
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  const isLanguageSettingsEnabled = useBooleanFlagValue(
    DICTATION_LANGUAGE_SETTINGS_FLAG_KEY,
    false
  );
  return isPlatformDictationEnabled && isLanguageSettingsEnabled;
};

/**
 * Non-hook companion to `useIsDictationLanguageSettingsFlagEnabled`. Use in
 * helpers/utils where hooks aren't available (e.g. Zustand-store paths that
 * build the dictation transcribe request).
 */
export const isDictationLanguageSettingsEnabled = () => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  return (
    IS_PLATFORM_DICTATION_ENABLED() &&
    client.getBooleanValue(DICTATION_LANGUAGE_SETTINGS_FLAG_KEY, false)
  );
};

const DICTATION_OUTPUT_LANGUAGE_FLAG_KEY = 'release-scribe-dictation-output-language';

/**
 * Gates the dictation output-language override (translation). Nested under
 * `release-dictation-language-settings`: the output control only renders when
 * both this flag and the language-settings flag are on.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, same shape and same disqualifier as the
 * language-settings gate above: it ANDs the PostHog-resolved platform-dictation
 * getter, so the composition is not door-readable.
 */
export const useIsDictationOutputLanguageFlagEnabled = () => {
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  const isOutputLanguageEnabled = useBooleanFlagValue(
    DICTATION_OUTPUT_LANGUAGE_FLAG_KEY,
    false
  );
  return isPlatformDictationEnabled && isOutputLanguageEnabled;
};

/**
 * Non-hook companion to `useIsDictationOutputLanguageFlagEnabled`. Use in
 * helpers/store code that builds the transcribe `user_context` outside React
 * (e.g. `getDefaultDictationOutputLanguages`).
 */
export const isDictationOutputLanguageEnabled = () => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  return (
    IS_PLATFORM_DICTATION_ENABLED() &&
    client.getBooleanValue(DICTATION_OUTPUT_LANGUAGE_FLAG_KEY, false)
  );
};

/**
 * MOVED to `@heidi/dictate` by ruling 36 — the key/default pair is declared
 * and read once in `packages/dictate/src/flags.ts`. The private key constant
 * went with it: leaving a copy here would be the second minting site the ruling
 * exists to refuse.
 */
export { useIsDictationVerbalCommandsMultilingualEnabled } from '@heidi/dictate/flags';

/**
 * Customizable dictation hotkey settings (desktop only). Requires Tauri + the
 * desktop-dictation parent flag. The former `release-dictation-hotkey-settings`
 * OpenFeature gate shipped to 100% and was removed.
 */
export const useIsDictationHotkeySettings = () => {
  const desktopDictationEnabled = useFeatureFlagEnabled(DESKTOP_DICTATION_FLAG) ?? false;
  return isTauri() && desktopDictationEnabled;
};

/**
 * Non-hook companion to `useIsDictationHotkeySettings`. Use in helpers/utils
 * where hooks aren't available (e.g. `getDictationHotkey`).
 */
export const isDictationHotkeySettingsEnabled = () => {
  if (!isTauri()) return false;
  return posthog.isFeatureEnabled(DESKTOP_DICTATION_FLAG) ?? false;
};

/**
 * Shipped 100% (Flagsmith). Semantic alias: per-user dictation log opt-out UI
 * and `log_dictations_enabled` on transcribe requests when platform dictation
 * is on. Non-hook for Zustand stores and Tauri config.
 */
export const isDictationLogOptOutEnabled = () => IS_PLATFORM_DICTATION_ENABLED();

/**
 * Shipped 100% (Flagsmith). Alias for platform dictation rollout in settings UI.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, and it
 * fails the move test twice over. It is a bare ALIAS of the PostHog-resolved
 * platform-dictation getter, so it reads no flag of its own and there is no
 * key/default pair for a descriptor to hold; and by noun it is dictation's
 * rather than workspace's, so even once the platform gate is door-readable it
 * belongs to that domain's lane.
 */
export const useIsDictationLogOptOutEnabled = useIsPlatformDictationEnabled;

/**
 * Effective adaptive-learning state: the `release-scribe-adaptive-learning`
 * master flag AND the persisted Labs opt-in — the FE mirror of the backend's
 * `is_adaptive_learning_active` (flag off is the kill switch: surfaces that
 * hide legacy personalisation must reappear when the flag is pulled, even for
 * users whose stored opt-in remains true).
 */
export const useIsAdaptiveLearningEnabled = () => {
  const flagValue = useBooleanFlagValue('release-scribe-adaptive-learning', false);
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.adaptiveLearning);
  return flagValue && optIn === true;
};

/**
 * Flagsmith kill switch for the Experimental Features Supplemental Terms gate
 * (AGE-1902). Gates both surfaces: the app-wide enforcement prompt and the
 * settings-page toggle intercept.
 *
 * The gate is a blocking, non-dismissible modal whose Decline can revoke a
 * user's experimental features, so it needs a remote off switch rather than a
 * revert-and-redeploy — a review pass already found it firing over an active
 * consult when `recording_status` had not resolved. OFF (default) means no
 * terms are required, so ship OFF, verify, then flip.
 *
 * Rollout rule: never revert this flag while it is open — re-segment first.
 */
export const useIsLabsExperimentalTermsGateEnabled = () =>
  useBooleanFlagValue('release-scribe-labs-experimental-terms', false);

/**
 * The Session Page V2 kill switch itself, WITH its last online decision
 * replayed while the flag provider is unreachable — the same treatment
 * `useIsNewSessionListEnabled` gets above, and for the same reason.
 *
 * Only the layout gate (here) and the invite ({@link useSessionPageV2InviteVariant})
 * read this replay. The Labs experiment card (`useExperiments`) and the
 * avatar-menu layout toggle stay on the live read, so they can disappear
 * mid-session while the replay still holds the layout up for the same user —
 * an accepted mixed state, not a bug. The invite deliberately remains visible
 * across an online-to-offline transition so the page does not change shape
 * mid-consult, even though its backend-writing actions cannot complete offline.
 * The Labs settings card keeps its live read because its opt-out cannot be
 * honoured offline.
 *
 * Flags composed with this gate remain live-only. Offline, the artifact pane
 * therefore falls back to V1 and the invite falls back to its Labs variant;
 * those safe mixed states are accepted rather than widening this cache beyond
 * the two layout flags involved.
 */
const useIsSessionPageV2FlagEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(SESSION_PAGE_V2_FLAG_KEY, false);
  const fromOfflineCache = useCachedFlagFallback(SESSION_PAGE_V2_FLAG_KEY);
  return fromFlagsmith || fromOfflineCache;
};

/**
 * Gates the Scribe session page V2 redesign — new IA and general UI improvements.
 *
 * V2 is built assuming the `/scribe/sessions/[id]` route is the entry point
 * (sessions list on the left, breadcrumb context, surrounding panel chrome).
 * It cannot render correctly on the legacy `/scribe/session/[id]` route, so
 * the dependency on `release-scribe-enable-new-session-list` is enforced
 * here: a V2 rollout that gets ahead of the new-list rollout falls back to
 * V1 silently rather than shipping broken UI. When the new-list flag reaches
 * 100% and is removed, drop this dependency too.
 *
 * All scribing logic (recording, transcription, note gen, chat) is reused;
 * only the visual layout forks. The single swap point lives inside `SessionPageContent`.
 *
 * `release-scribe-session-page-v2` gates every V2 surface — the layout (here),
 * the Labs experiment card (`useExperiments`), the avatar-menu layout toggle,
 * and the V1 opt-in journey ({@link useSessionPageV2InviteVariant}) — so
 * flipping it OFF is the ONLINE kill switch: the toggle disappears and
 * opted-in users revert to V1 once Flagsmith answers. Offline, it is not
 * immediate for every surface: the layout gate replays its last ON decision
 * (`useIsSessionPageV2FlagEnabled` above) and holds V2 up until the provider
 * next answers — the Labs card and the avatar-menu toggle stay on the live
 * read and can flip back mid-session even while the replay still holds the
 * layout up for the same user, an accepted mixed state. The follow-up banner
 * has an additional independent rollout switch,
 * `release-scribe-session-page-v2-banner`. Within the main flag's reach, V2 is
 * **opt-in-required**: the layout only turns on once the user explicitly opts
 * in via Heidi Labs (`optIn === true`); an undecided user stays on V1 and sees
 * the invite journey selected by the GA change-management flag.
 */
export const useIsSessionPageV2Enabled = () => {
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const flagValue = useIsSessionPageV2FlagEnabled();
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.sessionLayoutV2);
  const isAutoEnrolled = useIsAutoEnrolledSessionLayoutV2();
  // Show V2 for auto-enrolled users or manual Labs opt-ins; the stamp wins over
  // a Labs opt-out. Online, the flag stays the kill switch (OFF reverts everyone
  // to V1); offline, its last decision holds the current layout until Flagsmith
  // next answers.
  // Because the stamp accumulates while the flag is OFF, ramp the flag in step
  // with the backend enrol flag so switch-on isn't a bulk flip.
  const showV2 = isAutoEnrolled || optIn === true;
  return isNewSessionListEnabled && flagValue && showV2;
};

export const SESSION_PANEL_AI_COLUMN_FLAG_KEY = 'release-scribe-session-panel-ai-column';

/**
 * Hosts the in-session AI surface (Work / Ask Heidi) inside the V2 session-panel
 * column, so switching to it crossfades in place instead of collapsing one
 * panel while a second, differently-sized one expands beside it.
 *
 * ANDed with the V2 gate because the column only exists on V2 — the page-level
 * `EvidenceSidebarPanel` remains the surface everywhere else, and on V2 whenever
 * the shell is too narrow to hold a column at all.
 */
export const useIsSessionPanelAiColumnEnabled = () => {
  const isSessionPageV2Enabled = useIsSessionPageV2Enabled();
  const flagValue = useBooleanFlagValue(SESSION_PANEL_AI_COLUMN_FLAG_KEY, false);
  return isSessionPageV2Enabled && flagValue;
};

/**
 * Work-surface kill switch for the focused artifact pane. This rollout is
 * independent of Session Layout V2 eligibility, so it can be enabled for Work
 * users who still land on the legacy session page.
 */
export { useIsWorkSessionArtifactV2Enabled } from '@heidi/agents/flags';

export const SESSION_PAGE_V2_GA_CHANGE_MANAGEMENT_FLAG_KEY =
  'release-scribe-session-page-v2-ga-change-management';
export const SESSION_PAGE_V2_BANNER_FLAG_KEY = 'release-scribe-session-page-v2-banner';

export const useIsSessionPageV2BannerEnabled = () =>
  useBooleanFlagValue(SESSION_PAGE_V2_BANNER_FLAG_KEY, false);

export const useIsSessionPageV2GaChangeManagementEnabled = () =>
  useBooleanFlagValue(SESSION_PAGE_V2_GA_CHANGE_MANAGEMENT_FLAG_KEY, false);

export type SessionPageV2InviteVariant = 'labs' | 'ga';

/**
 * Selects the V2-layout invite shown on the legacy (V1) layout.
 *
 * The GA flag changes the journey rather than replacing its Labs predecessor:
 * OFF keeps the original card, whose dismiss action persists an explicit Labs
 * opt-out; ON selects the GA card, whose delay action leaves the tri-state
 * undecided and persists the invite as seen so the V1 banner can take over.
 * Auto-enrolled and explicitly decided users need neither card.
 */
export const useSessionPageV2InviteVariant = (): SessionPageV2InviteVariant | null => {
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.sessionLayoutV2);
  const flagValue = useIsSessionPageV2FlagEnabled();
  const isGaChangeManagementEnabled = useIsSessionPageV2GaChangeManagementEnabled();
  const isAutoEnrolled = useIsAutoEnrolledSessionLayoutV2();
  const isEligible =
    isNewSessionListEnabled && optIn === undefined && flagValue && !isAutoEnrolled;

  if (!isEligible) return null;
  return isGaChangeManagementEnabled ? 'ga' : 'labs';
};

/**
 * APP-5130: Gates the proactive firmware startup check on Desktop.
 * When enabled, the app automatically shows the firmware update modal
 * once per sign-in session when a Chronicle device connects with an
 * available update — without requiring the user to navigate to settings.
 */
export const useIsFirmwareStartupCheckEnabled = () => {
  return useBooleanFlagValue('release-firmware-startup-check-desktop', false);
};

/**
 * Evidence query limits for legacy billing tiers — the in-session limit
 * derivation, the chat stream's limit read and the "Usage & Billing" row.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`. Its key is `release-ml-scribe-`
 * rather than `release-evidence-`, which predates the namespace convention;
 * ownership followed the four consumers, all of which are evidence surfaces, and
 * the key itself is frozen because Flagsmith targets the flag by it.
 */
export { useIsEvidenceQueryLimitsForLegacyTiersEnabled } from '@heidi/evidence/flags';

/**
 * Comms Log Table Summary Header Column: Enable the summary header column in the comms log table.
 *
 * MOVED to `@heidi/calls` by ruling 36's fan-out batch 1 — declared
 * and read once in `packages/calls/src/flags.ts`.
 */
export { useIsCommsLogTableSummaryHeaderEnabled } from '@heidi/calls/flags';

export const isMediaRecorderTimesliceEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-mediarecorder-timeslice',
    false
  );
};

/**
 * EVI-1349: Gates the VisualDx interactive widget (4-step diagnostic flow +
 * ranked differential card) in Evidence chat. Defense-in-depth alongside the
 * BE flag of the same name — when this is false, the FE never renders an
 * `InteractiveWidgetBlock` even if a stale or replayed `interactive_widget`
 * chunk reaches the timeline.
 *
 * Placed at the end of the file by convention so future flag PRs that also
 * append at the end produce mechanical "keep both" merge conflicts rather
 * than the mid-file insertion collisions that bit EVI-1349 vs GRO-2896.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export {
  useIsEvidenceSubagentResidualCapabilitiesEnabled,
  useIsPartnerNejmFiguresEnabled,
  useIsVisualDxEnabled,
  useIsVisualDxModulesEnabled,
} from '@heidi/evidence/flags';

/**
 * Gates the integration agent surfaces.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved via
 * `useFeatureFlagEnabled`, which reads posthog-js on cloud and only falls back to
 * OpenFeature on the airgapped build; `@heidi/runtime/feature-flags` carries no
 * PostHog surface, so moving it would change WHICH PROVIDER answers
 * (`monorepo-wave-1-p3-feature-flags` Z4). It is also the one integrations flag
 * whose surface the on-prem appliance DOES admit, which is why every descriptor
 * that did move omits `builds` while this one would have needed `'all'`.
 */
export const useIsIntegrationAgentEnabled = () => {
  return useFeatureFlagEnabled('release-integration-agent') ?? false;
};

/**
 * Hides the Epic push button for the `disable-push-to-epic` cohorts. The flag is
 * person-keyed and flips true 0.1-2s after first render, so callers must subscribe.
 */
export const useIsDisablePushToEpic = () => {
  return useFeatureFlagEnabled(DISABLE_PUSH_TO_EPIC_FLAG_KEY) ?? false;
};

/**
 * When ON, keeps notes as plain markdown for Epic EHR Launch (web) and Haiku —
 * skipping the markdown→sectional conversion that `useJsonTemplateDefault`
 * normally enables for those surfaces — and forces "sync whole note" as the
 * only push option (no sectional/whole-note dropdown). When OFF, existing
 * sectional generation + dropdown behavior is preserved.
 *
 * Non-hook companion (`isEpicLaunchMarkdownNoteEnabled`) exists because the
 * legacy stream-response pipeline reads the JSON-template signal outside React.
 *
 * The second name is also the Epic push-note split button's option-label flag:
 * when ON it labels its primary action with the currently selected push option
 * ("Push sections to {Epic}" / "Push whole note to {Epic}") instead of the static
 * "Push to {Epic}", and it is read only inside `PushEpicSdesComponent`, which
 * already waits on `usePosthogFlagsReady` for the sibling sectional flag.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`, where the hook and the
 * non-React reader take the SAME descriptor. Both key constants were
 * module-private here rather than barrel exports, so they became descriptors with
 * no line of their own; the non-React reader's one disclosed delta is that
 * `readFlag` honours the injected `forceEnableAll`, which the raw
 * `OpenFeature.getClient(...).getBooleanValue` read bypassed.
 */
export {
  isEpicLaunchMarkdownNoteEnabled,
  isHyperspaceWholeNoteViewEnabled,
  useIsEpicLaunchMarkdownNoteEnabled,
  useIsEpicPushNoteButtonLabelByOptionEnabled,
} from '@heidi/integrations/flags';

/**
 * Gates the global "Talk to Heidi" voice assistant (floating button + modal)
 * and the workspace window / placement. Same key the backend WS route gates on.
 *
 * MOVED to `@heidi/agents` by ruling 36's whole-class fan-out, along with the
 * non-React reader that used to build its own OpenFeature client at
 * `src/features/voice-assistant/workspace/workspaceFlag.ts` — one descriptor
 * now, three readers.
 *
 * IMPORTED, not just re-exported, because `useIsVoiceDictationModeEnabled`
 * below composes it and stays here: that gate ANDs a PostHog-resolved flag, and
 * `@heidi/runtime/feature-flags` carries no PostHog surface.
 *
 * Its `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` escape hatch COLLAPSED onto
 * the door's injected `forceEnableAll`.
 */
export { useIsVoiceAssistantEnabled };

/** Desktop Hey Heidi wake-word rollout gate, declared in `@heidi/agents`. */
export { useIsAmbientHeidiEnabled } from '@heidi/agents/flags';

/**
 * Same Flagsmith key as `useIsVoiceAssistantEnabled`. Kept as a named hook
 * because placement / workspace chrome already call it; ON now always means
 * widget + window + placement together.
 *
 * MOVED to `@heidi/agents` by ruling 36's whole-class fan-out.
 */
export { useIsVoiceWorkspaceEnabled } from '@heidi/agents/flags';

export {
  HEIDI_2_WELCOME_CTA_FLAG_KEY,
  useHeidi2WelcomeCtaCopy,
} from '@/features/flags/heidi-2-welcome-cta-flag';

/**
 * Gates the Talk-to-Heidi dictation mode surface (Start dictation control +
 * capture bar). PostHog-backed against the same
 * `release-ml-scribe-voice-dictation-mode` flag the backend tools/frames gate
 * on, ANDed with the umbrella voice-assistant flag so a dictation bug can be
 * killed without disabling the whole assistant — and so the button never
 * appears before the backend understands `start_dictation`. Defaults OFF.
 */
export const useIsVoiceDictationModeEnabled = () => {
  const voiceOn = useIsVoiceAssistantEnabled();
  const dictationOn =
    useFeatureFlagEnabled('release-ml-scribe-voice-dictation-mode') ?? false;
  return voiceOn && dictationOn;
};

/**
 * EVI-1566 (EV-020): Gates the active risk opt-in shown before a user-library
 * document upload proceeds. When ON, clinicians must explicitly accept the
 * upload risks (they remain responsible for clinical safety; Evidence cannot
 * reliably interpret images and diagrams) before any file is uploaded.
 * Acceptance is persisted on the user record (EVI-1698). Default OFF.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsLibraryUploadRiskOptinEnabled } from '@heidi/evidence/flags';

/**
 * EVI-1566 (EV-029): Gates the image/OCR quality-check UI on user-library
 * document uploads. When ON, each successful upload surfaces a simulated OCR
 * quality outcome (ok / low-quality warning / unreadable error) so unreadable
 * uploads are not silently trusted. The simulation is FE-only for now; the
 * real backend quality scan replaces it in a follow-up. Default OFF.
 *
 * MOVED to `@heidi/evidence` by ruling 36's fan-out batch 3 — declared and read
 * once in `packages/evidence/src/flags.ts`.
 */
export { useIsLibraryUploadQualityChecksEnabled } from '@heidi/evidence/flags';

/**
 * When ON, clinical task title and description fields use a TipTap-based
 * rich-text editor with markdown support instead of plain textareas.
 *
 * Backward compatible: plain text is valid markdown, so existing tasks
 * render correctly in the new editor without migration.
 *
 * MOVED to `@heidi/tasks` by ruling 36's fan-out batch 1 — declared
 * and read once in `packages/tasks/src/flags.ts`. Three of its four production
 * consumers sit under `src/features/integrations-letters/letter-sender-v2/**`; the flag
 * is tasks-owned by key and by noun, so those are cross-root READS that stay
 * where they are and resolve through this re-export.
 */
export { useIsClinicalTaskRichTextEnabled } from '@heidi/tasks/flags';

/**
 * Gates the region-onboarding self-heal guard (`useRequireRegionGuard`). ON
 * reroutes authenticated users whose Kinde properties resolve with no region to
 * `/scribe/new-user`; OFF (default) leaves the guard inert, which is what makes
 * it an instant kill switch for the redirect.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out.
 */
export { useIsRegionOnboardingGuardEnabled } from '@heidi/growth/flags';

/**
 * Gates the markdown-native editor rewrite (Labs availability on web): the note
 * editor, and the template editor plus its read-only markdown preview.
 * Default OFF; flipped per-environment in Flagsmith — Labs cohort only at MS1.
 *
 * One flag on purpose. Template content and note content are edited by the same
 * kit, so splitting the gate would let a clinician's template render lists and
 * `##` headings their note editor still drops (SCR-5881).
 *
 * Non-hook companion (`isMarkdownNativeEditorEnabled`) exists for plain-JS
 * call sites such as TipTap extension factories and markdown helpers, which
 * are not React components.
 */
export const useIsMarkdownNativeEditorEnabled = () => {
  return useBooleanFlagValue('release-scribe-enable-markdown-native-editor', false);
};

/**
 * SCR-7862 kill switch for the Copy settings hints on the note and document
 * toolbars: the discovery coachmark and the formatting-loss popover. Flip ON
 * to turn both off without turning off improved copy. Copy itself, the Copy
 * settings menu and its toggles are unaffected.
 *
 * Default OFF, so an absent flag, an unset flag, the loading window and the
 * on-prem bundle all keep the hints as shipped. A raw read on purpose: the
 * door's `forceEnableAll` dev knob would otherwise turn the hints off.
 * Kill gate, not an experiment, so no `useManualExposure`.
 */
export const useIsCopySettingsHintsKilled = () => {
  return useBooleanFlagValue('kill-scribe-copy-settings-hints', false);
};

/**
 * Gates the redesigned note-gen loading experience on the live note surface:
 * the editor stays mounted through generation with a floating status row
 * overlaying it, replacing the full-canvas `Loading` takeover and the bottom
 * "Stop generating" bar. Default OFF.
 *
 * Applies to EVERY note editor variant, not just markdown-native — turning this
 * on is not inert for rich-text/textarea cohorts. Markdown-native fills the note
 * in live as tokens stream; the others keep whatever is already on the canvas
 * visible instead of blanking it, which is the point of the redesign either way.
 */
export const useIsNoteGenLoadingV2Enabled = () => {
  return useBooleanFlagValue('release-scribe-note-gen-loading-v2', false);
};

/**
 * Whether a flag read reflects the user's real assignment yet.
 *
 * The provider is installed fire-and-forget, so every `useBooleanFlagValue`
 * returns its default until resolution lands — fine for gating a surface, which
 * simply re-renders, but wrong for *reporting* a cohort, where the pre-resolution
 * default can be recorded permanently.
 *
 * Any settled state (READY / STALE / ERROR / FATAL) counts as resolved, matching
 * `useRegionalUpdaterGate`: a flag-service outage falls back to the default rather
 * than suppressing the report forever.
 */
export const useIsFlagCohortResolved = (): boolean => {
  const status = useOpenFeatureClientStatus();
  return status !== ProviderStatus.NOT_READY && status !== ProviderStatus.RECONCILING;
};

export const isMarkdownNativeEditorEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-enable-markdown-native-editor',
    false
  );
};

/**
 * UMBRELLA gate for the ENTIRE Scribe Canvas flexible-artefact FE experience:
 * type-into-sections, blank documents, the searchable "+ Create" menu, and
 * dictation-as-a-canvas-artefact. Deliberately distinct from the backend
 * rollout flag `release-scribe-flexible-artefact-generation` so the two roll out
 * independently. Rollout/kill gate, NOT an experiment — do not add
 * useManualExposure.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out — the hook, its
 * non-hook companion for TipTap extension factories and markdown helpers, and
 * the key all take ONE descriptor. The companion's own
 * `OpenFeature.getClient(FEATURE_FLAG_DOMAIN)` became the door's `readFlag`,
 * which resolves the same client on the same default domain.
 *
 * BOTH bodies' `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` branches COLLAPSED
 * onto the door's injected `forceEnableAll`.
 */
export {
  isScribeCanvasExperienceEnabled,
  SCRIBE_CANVAS_EXPERIENCE_FLAG_KEY,
  useIsScribeCanvasExperienceEnabled,
} from '@heidi/session/flags';

/**
 * Gates the Kinde → Auth0 Apple migration flow: when enabled, eligible Kinde
 * users have the assign-apple-migration-ticket endpoint called and are shown
 * the forced migration modal. Off by default (opt-in rollout).
 *
 * Platform-split flags so the two ramps are fully decoupled:
 *   - Desktop (Tauri): `release-auth0-apple-migration-ticket-desktop`
 *   - Web: `release-auth0-apple-migration-ticket-web`
 *
 * Hook-order note: both hooks are called unconditionally, so hook order is
 * stable across renders regardless of ``isTauri()``.
 */
export const useIsAuth0AppleMigrationTicketEnabled = () => {
  const isWebEnabled = useBooleanFlagValue(
    'release-auth0-apple-migration-ticket-web',
    false
  );
  const isDesktopEnabled = useBooleanFlagValue(
    'release-auth0-apple-migration-ticket-desktop',
    false
  );
  return isTauri() ? isDesktopEnabled : isWebEnabled;
};

/**
 * Gates the session-page Cmd/Ctrl+A override that scopes Select-All to the
 * active tab pane instead of the whole document. Kill switch: when OFF,
 * native browser Select-All applies everywhere. Default OFF.
 */
export const useIsScopedSelectAllEnabled = () => {
  return useBooleanFlagValue('release-scribe-scoped-select-all', false);
};

/**
 * Workspace switcher — gates the sidebar workspace switcher menu (and the wider
 * M8 UI series). Single point every M8 surface checks the flag; defaults OFF so
 * the menu only renders for rolled-out cohorts.
 *
 * MOVED to `@heidi/workspace` by ruling 36's fan-out batch 2 — declared and read
 * once in `packages/workspace/src/flags.ts`. Published from a local binding
 * rather than a one-line `export … from` because the combined gate below
 * composes it; see the import at the top of this file.
 */
export { useIsWorkspaceSwitcherEnabled };

/**
 * Raw `release-workspace-settings` flag. Defaults OFF.
 *
 * Prefer `useIsWorkspaceSettingsIaEnabled` for settings IA surfaces — that
 * combined gate is ON only when this flag AND `useIsWorkspaceSwitcherEnabled`
 * are both on. Reach for this hook only when you need the raw flag value
 * (e.g. composing the combined gate itself).
 *
 * MOVED to `@heidi/workspace` by ruling 36's fan-out batch 2. No production file
 * outside this barrel reads it directly, and that is recorded rather than acted
 * on: its one reader is the combined gate below, which sixteen production files
 * read. A getter nobody reads directly is a retirement candidate, but retiring a
 * flag is a `docs/feature-flag-cleanups.md` change with a provider step behind
 * it — a different decision from where the declaration lives — so it moved
 * rather than being deleted.
 */
export { useIsWorkspaceSettingsEnabled };

/**
 * Combined gate for the workspace settings IA. Every settings-reorganisation
 * surface (route-based sidebar split, workspace-scoped header, Profile split)
 * must gate on this: it is ON only when BOTH the workspace switcher and the
 * workspace settings flags are on. Single source of truth so surfaces don't
 * re-derive the `switcher && settings` combination inline.
 *
 * STAYS HERE, and it is a deliberate reading of the fan-out's rule rather than
 * an oversight: a getter moves when its body READS a flag and nothing else, and
 * this one declares no key and reads none — it composes two getters that did
 * move. Composing a domain's OWN getters may travel into the module, so relocating
 * it later is legal; it is simply not this batch's unit of work, and nothing a
 * consumer can see turns on where it lives.
 */
export const useIsWorkspaceSettingsIaEnabled = () => {
  const isSwitcherEnabled = useIsWorkspaceSwitcherEnabled();
  const isSettingsEnabled = useIsWorkspaceSettingsEnabled();
  return isSwitcherEnabled && isSettingsEnabled;
};

/**
 * Whether a cloud flag provider is configured at all. Mirrors the key check in
 * `registerFlagProviders`: without it nothing is ever registered. Read at call
 * time, not module scope, so it stays testable — Next.js inlines the literal
 * either way. Exported for identity-aware gates that must fail open when no
 * provider will ever settle (local dev without a Flagsmith key).
 */
export const isFlagProviderConfigured = () =>
  Boolean(process.env.NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY);

/**
 * True once the OpenFeature provider has settled — any status other than
 * NOT_READY, including the fail-open ERROR state and on-prem. Deliberately NOT
 * READY-only: the app parks the flag domain at ERROR as a settled fail-open
 * state (Flagsmith init bounded then rejected, on-prem bundle), so a READY-only
 * gate would strand callers forever. Use to hold a flag-driven redirect or paint
 * until the flag can be read for real, never longer.
 *
 * RECONCILING and the pre-identify READY both count as settled, so a caller can
 * still read a value evaluated against an earlier context. That window predates
 * this hook — the alternative is a caller that never releases — and closing it
 * needs an identity-aware readiness check, not a stricter status test.
 */
// TODO: fold useIsFlagCohortResolved into this (it is this hook plus a
// RECONCILING exclusion) so it inherits the on-prem/unconfigured handling.
export const useIsFlagProviderSettled = () => {
  const providerStatus = useOpenFeatureClientStatus();
  // No environment key ⇒ `registerFlagProviders` never calls `setProvider`, so
  // the status is NOT_READY for the life of the tab and the bounded-init
  // timeout that would otherwise release the caller is never constructed.
  if (isOnPrem || !isFlagProviderConfigured()) return true;
  return providerStatus !== ProviderStatus.NOT_READY;
};

/**
 * True when the flag provider is in its settled fail-open ERROR state (e.g.
 * Flagsmith init timed out). Identity-aware gates treat this like "no answer"
 * and fail open rather than parking on pending.
 */
export const useIsFlagProviderFailed = (): boolean =>
  useOpenFeatureClientStatus() === ProviderStatus.ERROR;

/**
 * The `targetingKey` last observed on a READY (or ConfigurationChanged-while-
 * READY) event for the flag domain. Identity-aware gates compare this to the
 * current `user_ref_id` so a pre-identify READY default is not treated as a
 * settled answer for the signed-in user.
 */
export const useAppliedFlagTargetingKey = (): string | undefined => {
  const [appliedTargetingKey, setAppliedTargetingKey] = useState<string | undefined>();

  useEffect(() => {
    const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
    const syncIfReady = () => {
      if (client.providerStatus !== ProviderStatus.READY) return;
      setAppliedTargetingKey(OpenFeature.getContext().targetingKey);
    };
    syncIfReady();
    client.addHandler(ProviderEvents.Ready, syncIfReady);
    client.addHandler(ProviderEvents.ConfigurationChanged, syncIfReady);
    return () => {
      client.removeHandler(ProviderEvents.Ready, syncIfReady);
      client.removeHandler(ProviderEvents.ConfigurationChanged, syncIfReady);
    };
  }, []);

  return appliedTargetingKey;
};

/**
 * The ONE place the post-login workspace selector's flag is read, so the boot
 * writer (`useHydrateWorkspaceId`, above the hydration gate) and
 * `useWorkspaceHydrationStatus` — which receives THIS hook through the
 * `configureWorkspaceData` seam — can never disagree about whether the default
 * pick is deferred. The return shape is the seam's `MultiWorkspaceSelectionGate`.
 *
 * Composes `useIsFlagProviderSettled`, NOT `useAreFeatureFlagsResolved`: the
 * latter is `false` for the life of the tab when no provider is registered (no
 * environment key locally; on-prem), which would hold every multi-workspace user
 * on the loader until the watchdog degraded them.
 *
 * STAYS HERE by the same reading as `useIsWorkspaceSettingsIaEnabled`: it
 * declares no key and reads none — it composes a domain getter with the app's
 * provider-readiness read, which a package cannot make (it reads env).
 */
export const useMultiWorkspaceSelectionGate = (): MultiWorkspaceSelectionGate => {
  const isEnabled = useIsMultiWorkspaceMembershipEnabled();
  const isSettled = useIsFlagProviderSettled();
  return {
    isSelectionEnabled: isSettled && isEnabled,
    isAwaitingFlag: !isSettled,
    shouldDeferInitialPick: !isSettled || isEnabled,
  };
};

/**
 * Routines/Workflows UI: alias of `useIsProjectsEnabled` (BE
 * `is_workflows_enabled` mirrors).
 */
export { useIsWorkflowsEnabled };

/**
 * The session-scoped routine-run getter and legacy key.
 *
 * MOVED to `@heidi/agents` by ruling 36's whole-class fan-out — the key is
 * minted once in `packages/agents/src/flags.ts` and this constant is DERIVED
 * from that descriptor there (`sessionRoutineRunsFlag.key`), then re-exported
 * here. Re-declaring the literal on this line would be the second minting site
 * the ruling exists to refuse, which
 * `scripts/check-flag-getter-doors.test.ts` is the only guard able to see.
 *
 * Runtime enablement follows the Heidi Work umbrella (`useIsProjectsEnabled`);
 * the old child key remains exported only for flag-inventory compatibility.
 */
export { SESSION_ROUTINE_RUNS_FLAG_KEY } from '@heidi/agents/flags';
export { useIsSessionRoutineRunsEnabled };

/**
 * Gates showing the calendar event title (Google Calendar invite `summary`)
 * as the primary label on the Schedule surfaces — the side-nav Schedule list
 * (`EhrScheduleItem`) and the appointment-list table column. When OFF
 * (default) those surfaces fall back to the patient name, preserving the
 * pre-rollout behaviour. Rollout/kill gate — flipped per-environment in
 * Flagsmith.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`.
 */
export { useIsCalendarEventTitleEnabled } from '@heidi/integrations/flags';

/*
 * Gates the new "For action" session review status across the apply and filter
 * surfaces (session header, V2 status row, session-list inline picker, Action
 * Center, and the status filters). Rollout / kill gate — not an experiment, so
 * no `useManualExposure`.
 *
 * PostHog-backed (`useFeatureFlagEnabled`) so the rollout can be ramped and
 * targeted from PostHog. The backend has no gate on this status — it accepts and
 * returns `FOR_ACTION` unconditionally — so this purely controls FE selection.
 *
 * Display is intentionally NOT gated — `sessionStatusChipConfig` and
 * `enumToDescription` always resolve `FOR_ACTION`, so a session already set to
 * "For action" still renders its chip/label/colour when the flag is off; only
 * the ability to *select* or *filter by* it is gated.
 */
export const useIsForActionSessionStatusEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-enable-for-action-status') ?? false;
};

/**
 * Gates lifting Haiku note-generation orchestration (polling +
 * terminate + note-gen kickoff) into a single stable session-scoped owner so it survives
 * tab switches. Default OFF = legacy per-consumer instantiation (today's behaviour).
 * Kill switch / rollout gate, NOT an experiment — do not add useManualExposure.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`.
 */
export { useIsHaikuStableOrchestrationEnabled } from '@heidi/integrations/flags';

/**
 * When the clinician's user settings have not loaded in the Haiku webview by
 * the time Start is tapped, show the patient consent popup instead of
 * skipping it (INT-5253 follow-up — BILH sessions recorded with no popup).
 * Default OFF = legacy behaviour (unknown settings treated as popup-off).
 * Kill switch / rollout, NOT an experiment — do not add useManualExposure.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`.
 */
export { useIsHaikuConsentPopupOnUnknownSettingsEnabled } from '@heidi/integrations/flags';

/**
 * Per-team rollout gate for the Haiku block screen shown when the BE returns
 * no `session_id`. Default OFF; do not add useManualExposure. DEV-12835.
 */
export const useIsHaikuBlockMessageEnabled = () => {
  return useBooleanFlagValue('release-integrations-haiku-block-message', false);
};

/**
 * After Haiku stop, start note writing directly instead of the shared desktop
 * "after recording" path (that path can open a template picker Haiku can't use
 * and leave the UI stuck on "generating"). Also unsticks sooner when the backend
 * never started a note job. Kill switch / rollout, default OFF.
 * Not an experiment — do not add useManualExposure.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`, where the hook and the
 * SCREAMING_SNAKE non-React reader (`scribeRecordModel` reads it outside React)
 * take the SAME descriptor. The reader's one disclosed delta is that `readFlag`
 * honours the injected `forceEnableAll`, which the raw
 * `OpenFeature.getClient(...).getBooleanValue` read bypassed.
 *
 * THE KEY MOVED WITH THEM AND IS STILL EXPORTED HERE, derived from the descriptor
 * rather than re-declared: minting the literal a second time on this line is the
 * defect ruling 36 exists to refuse, and the door-spanning check in
 * `scripts/check-flag-getter-doors.test.ts` is what refuses it.
 */
export {
  HAIKU_NOTE_PICKER_DIRECT_DISPATCH_FLAG_KEY,
  IS_HAIKU_NOTE_PICKER_DIRECT_DISPATCH_ENABLED,
  useIsHaikuNotePickerDirectDispatchEnabled,
} from '@heidi/integrations/flags';

/**
 * Gates the Haiku session-time rendering fix (INT-5282): when ON, the
 * `SessionInfoToolbar` renders `created_time_local` as a verbatim wall-clock
 * time (zone suffix stripped, no device-timezone conversion) so it matches the
 * Epic visit time. When OFF (default), the legacy UTC-aware rendering applies —
 * `created_time_local` is parsed as a UTC instant and converted to the device
 * timezone. Kill switch / rollout gate, NOT an experiment — do not add
 * useManualExposure.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`. Its key is
 * UNDERSCORE-separated where every neighbour is hyphenated; that is what
 * Flagsmith targets, so the package transcribes it exactly and asserts the
 * spelling.
 */
export { useIsHaikuNoteTimeRenderingEnabled } from '@heidi/integrations/flags';

/**
 * Experiment: show an Integrations button in the sidebar nav.
 * Flagsmith key: `exp-integration-button-in-sidebar`.
 *
 * MOVED to `@heidi/integrations` by ruling 36's fan-out batch 3 — declared and
 * read once in `packages/integrations/src/flags.ts`. It carried no
 * `useManualExposure` pairing app-side and gains none: the exposure invariant is
 * about PRESERVING a pairing that exists, and adding one during a relocation
 * would start emitting an experiment exposure that has never been emitted.
 */
export { useIsIntegrationButtonInSidebarEnabled } from '@heidi/integrations/flags';

let hasWarnedMissingInboxServiceUrl = false;

/**
 * The inbox-service runs on its own origin; `NEXT_PUBLIC_INBOX_SERVICE_URL` is
 * REQUIRED to talk to it (`getDomains` does NOT fall back to the Scribe
 * gateway). Logged once so a misconfigured rollout is discoverable without
 * spamming on every render.
 */
const warnMissingInboxServiceUrl = () => {
  if (hasWarnedMissingInboxServiceUrl) return;
  hasWarnedMissingInboxServiceUrl = true;
  logger.error(
    '[document-inbox] release-scribe-enable-document-inbox is enabled but NEXT_PUBLIC_INBOX_SERVICE_URL is not set; the inbox UI is disabled. Configure the inbox-service origin to enable it.'
  );
};

/**
 * Tri-state gate for the Document Inbox umbrella flag. Mirrors
 * {@link RegionalUpdaterGate}: `'unknown'` while OpenFeature is NOT_READY /
 * RECONCILING so route guards do not treat an unresolved default-false read as
 * a settled OFF.
 */
export type DocumentInboxGate = 'on' | 'off' | 'unknown';

/**
 * Tri-state umbrella gate for the ENTIRE Document Inbox frontend surface.
 *
 * Prefer this over {@link useIsDocumentInboxEnabled} for route guards and any
 * surface that redirects when disabled: `'unknown'` means the OpenFeature
 * provider has not settled yet (NOT_READY / RECONCILING), so callers must wait
 * rather than treating the default-false flag read as a resolved OFF.
 *
 * ALL inbox UI MUST gate behind this hook (or the boolean convenience wrapper):
 * sidebar nav, `/inbox` + `/inbox/success` routes, Settings -> Inbox, and any
 * future inbox UI.
 *
 * Default OFF; flip per-environment in Flagsmith (staging first). Rollout/kill
 * gate — do not add useManualExposure.
 *
 * `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` bypasses OpenFeature (local dev).
 * Missing `NEXT_PUBLIC_INBOX_SERVICE_URL` when the flag is on resolves to
 * `'off'` (not `'unknown'`) — see {@link getDomains}.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, on THREE
 * counts, and it holds the three getters below it with the same reasons.
 * `NEXT_PUBLIC_INBOX_SERVICE_URL` is a bespoke env var that the door's injected
 * `forceEnableAll` does not reproduce and that a package may not read; the return
 * shape is a tri-state built from `useOpenFeatureClientStatus()`, which no door
 * reader publishes; and the force-all branch is not a plain collapse here — it
 * SKIPS the provider-readiness wait, which is behaviour rather than a dev escape
 * hatch. It moves when the inbox-service URL requirement leaves the gate, not
 * before.
 */
export const useDocumentInboxGate = (): DocumentInboxGate => {
  const flagStatus = useOpenFeatureClientStatus();
  const fromFlagsmith = useBooleanFlagValue(
    'release-scribe-enable-document-inbox',
    false
  );
  const forceAll = process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';

  // Local-dev force-all is an explicit OpenFeature bypass, so do not block on
  // provider readiness here. Keep the inbox-service URL requirement unchanged.
  if (forceAll) {
    if (!process.env.NEXT_PUBLIC_INBOX_SERVICE_URL) {
      warnMissingInboxServiceUrl();
      return 'off';
    }
    return 'on';
  }

  const isFlagResolved =
    flagStatus !== ProviderStatus.NOT_READY && flagStatus !== ProviderStatus.RECONCILING;

  if (!isFlagResolved) return 'unknown';

  const flagEnabled = fromFlagsmith;

  if (flagEnabled && !process.env.NEXT_PUBLIC_INBOX_SERVICE_URL) {
    warnMissingInboxServiceUrl();
    return 'off';
  }

  return flagEnabled ? 'on' : 'off';
};

/**
 * Boolean convenience wrapper around {@link useDocumentInboxGate}. Safe for
 * simple show/hide (e.g. nav items). Route guards that redirect when disabled
 * MUST use `useDocumentInboxGate` and wait on `'unknown'`.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. It composes the umbrella gate above, so it
 * inherits that gate's mechanism whole; there is no separate flag read here to
 * move.
 */
export const useIsDocumentInboxEnabled = () => useDocumentInboxGate() === 'on';

/**
 * Sub-feature gate for clinical tasks inside the Document Inbox: the Tasks tab,
 * suggestion accept, manual create, and the linked-task list.
 *
 * ml-scribe's own kill switch: with it OFF accept fails hard and the link
 * filter is silently ignored, so there is nothing worth rendering. Keep it
 * environment-level — the inbox-accept endpoint is on ml-scribe's internal
 * router, which evaluates flags without user context.
 *
 * ANDed with the umbrella {@link useDocumentInboxGate}: tasks are part of the
 * inbox and can never outlive it. Default OFF; flip per-environment in
 * Flagsmith (staging first). Rollout/kill gate — do not add useManualExposure.
 */
/**
 * Sub-feature gate for the "Was this extraction helpful?" thumbs + detail
 * popover next to the push-to-EHR CTA (UKI-751). FE-only analytics surface;
 * with the flag OFF nothing renders.
 *
 * ANDed with the umbrella {@link useDocumentInboxGate} so the surface can be
 * killed independently of the inbox. Default OFF; flip per-environment in
 * Flagsmith. Rollout/kill gate — do not add useManualExposure.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. This one is integrations-owned by key and
 * by surface, so the domain is not the blocker: the body ANDs
 * `useIsDocumentInboxEnabled()`, whose umbrella gate reads a bespoke env var and
 * a provider status the door has no reader for, so the composition inherits that
 * mechanism. It moves with the umbrella, not before.
 */
export const useIsInboxExtractionFeedbackEnabled = (): boolean => {
  const isInboxEnabled = useIsDocumentInboxEnabled();
  const forceAll = process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';
  const fromFlagsmith = useBooleanFlagValue(
    'release-inbox-enable-extraction-feedback',
    false
  );

  return isInboxEnabled && (forceAll || fromFlagsmith);
};

/**
 * Inbox list bulk selection and actions. Sub-flag of the document-inbox
 * umbrella so the destructive bulk surface can be rolled back independently.
 * Default OFF; rollout/kill gate — do not add useManualExposure.
 */
export const useIsInboxBulkActionsEnabled = (): boolean => {
  const isInboxEnabled = useIsDocumentInboxEnabled();
  const forceAll = process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';
  const fromFlagsmith = useBooleanFlagValue('release-inbox-enable-bulk-actions', false);

  return isInboxEnabled && (forceAll || fromFlagsmith);
};

/**
 * Clinical tasks inside the document inbox. Sub-flag of the document-inbox
 * umbrella so the surface can be rolled back independently of the inbox.
 * Rollout/kill gate — do not add `useManualExposure`.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than
 * skipped. `release-ml-scribe-enable-inbox-clinical-tasks` is a `@heidi/tasks`
 * flag by noun, but the body ANDs `useIsDocumentInboxEnabled()` — a FOREIGN
 * domain's getter (integrations / document-inbox). Ruling 36 (b) refuses that
 * import outright: a package would bind another domain's whole door for one
 * boolean and invert the dependency direction for it, and the resolved boolean
 * crosses as a prop at the composition point that already owns it. So the
 * composition stays here, where it is legal, and the forceAll branch stays with
 * it.
 *
 * The move this unblocks is not a package move: it is inverting the composition
 * so the inbox boolean is supplied at the composition point rather than read
 * inside the getter. That is a call-site change at the one production consumer,
 * and a separate decision from this batch.
 */
export const useIsInboxClinicalTasksEnabled = (): boolean => {
  const isInboxEnabled = useIsDocumentInboxEnabled();
  const forceAll = process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';
  const fromFlagsmith = useBooleanFlagValue(
    'release-ml-scribe-enable-inbox-clinical-tasks',
    false
  );

  return isInboxEnabled && (forceAll || fromFlagsmith);
};

/**
 * Inbox custom agents (UKI-814/815): the settings CRUD section and the
 * per-document results section. Sub-flag of the document-inbox umbrella so the
 * clinician-authored-agent surface can be rolled back independently of the
 * inbox. Default OFF; flip per-environment in Flagsmith. Rollout/kill gate —
 * do not add useManualExposure.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, same classification as
 * `useIsInboxExtractionFeedbackEnabled` above: integrations-owned, but it ANDs
 * the umbrella gate and inherits its mechanism.
 */
export const useIsInboxCustomAgentsEnabled = (): boolean => {
  const isInboxEnabled = useIsDocumentInboxEnabled();
  const forceAll = process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';
  const fromFlagsmith = useBooleanFlagValue('release-inbox-enable-custom-agents', false);

  return isInboxEnabled && (forceAll || fromFlagsmith);
};

/**
 * Inbox Ask Heidi / Evidence bar on the document detail page. Sub-flag of the
 * document-inbox umbrella so Evidence chat can be held back independently of
 * the inbox release. PostHog-backed (`useFeatureFlagEnabled`), default OFF.
 * Does not honour FORCE_ENABLE_ALL — inbox testers must not see this surface
 * until the PostHog flag is flipped. Also ANDs the session Ask Heidi kill
 * switch so EU-27 free/pro (and unresolved PostHog) stay excluded once the
 * holdback is lifted. Rollout/kill gate — do not add useManualExposure.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, same classification as
 * `useIsInboxExtractionFeedbackEnabled`: integrations-owned, but it ANDs the
 * umbrella gate and inherits its mechanism. Additionally PostHog (Z4) — a
 * package move would change which provider answers.
 */
export const useIsInboxAskHeidiEnabled = (): boolean => {
  const isInboxEnabled = useIsDocumentInboxEnabled();
  const isSessionAskHeidiBarDisabled = useIsSessionAskHeidiBarDisabled();
  const fromPosthog = useFeatureFlagEnabled('release-inbox-enable-ask-heidi') ?? false;
  return isInboxEnabled && fromPosthog && !isSessionAskHeidiBarDisabled;
};

/**
 * Gates every session "urgency" surface behind one flag: the session-header
 * chip (set), the session-list urgency filter (desktop + mobile + side-nav) and
 * its "Order by" sort option, AND the display surfaces — the row marker
 * (`SessionUrgencyFlag`, which self-gates) and the urgent row/card tint. With
 * the flag off, urgency is invisible and inert everywhere — nothing to set,
 * filter, sort, or display.
 *
 * PostHog-backed (`useFeatureFlagEnabled`) so it can be ramped/targeted from
 * PostHog and stays in lock-step with the backend (see
 * `docs/contracts/session-urgency.md`) — mirroring the For-action status flag.
 * Rollout/kill gate, not an experiment, so no `useManualExposure`.
 */
export const useIsSessionUrgencyEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-enable-session-urgency') ?? false;
};

/**
 * Gates the "Export as PDF/Word" font + size feature as one unit:
 *   - the in-export font/size picker modal (incl. "save as default, don't show
 *     again"),
 *   - applying a saved default + skipping the picker, and
 *   - the Settings > Defaults > Session defaults "Default export font" control.
 *
 * When OFF, exports use the legacy behaviour — no picker, no font applied, the
 * document keeps the renderer defaults. PostHog-backed so the rollout can be
 * ramped/targeted from PostHog. Rollout/kill gate, NOT an experiment — do not
 * add useManualExposure.
 */
export const useIsExportFontOptionsEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-export-font-options') ?? false;
};

/**
 * Gates the personalised "live transcription" treatment on the onboarding
 * mic-test step, which names the clinician's specialty in the heading before the
 * sample transcript plays. Rollout/kill gate, not an experiment — no
 * useManualExposure.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out, DEV-default
 * included — Flagsmith targeting is not configured for local and dev deploys,
 * so that branch is behaviour rather than a force-all hatch and travels with
 * the getter.
 */
export { useIsMicTestLiveTranscriptionEnabled } from '@heidi/growth/flags';

/**
 * Gates form editor (Field Recognition) multi-select on the PDF canvas as one
 * unit: marquee drag-select on the page background, shift-click add/remove,
 * group move (dragging any selected field moves the whole selection), and
 * Figma-style group resize via corner handles on the selection bounding box.
 * When OFF (default), the canvas keeps today's single-select behaviour exactly.
 * Rollout/kill gate, NOT an experiment — do not add useManualExposure.
 */
export const useIsFormEditorMultiSelectEnabled = () => {
  return useFeatureFlagEnabled('release-growth-form-editor-multi-select') ?? false;
};

/**
 * Gates form editor (Field Recognition) undo/redo as one unit: the Cmd/Ctrl+Z
 * and Shift+Cmd+Z / Ctrl+Y shortcuts plus the toolbar undo/redo buttons.
 * History recording itself always runs (cheap snapshot atoms) so flipping the
 * flag mid-session immediately exposes a working history.
 *
 * PostHog-backed (`useFeatureFlagEnabled`) so the rollout can be ramped and
 * targeted from PostHog. Defaults ON in the DEV environment (local + dev
 * deploy) so it is testable before the PostHog flag exists — mirrors
 * useIsMicTestLiveTranscriptionEnabled. Rollout/kill gate, NOT an
 * experiment — do not add useManualExposure.
 */
export const useIsFormEditorUndoRedoEnabled = () => {
  const flag = useFeatureFlagEnabled('release-growth-form-editor-undo-redo') ?? false;
  if (getEnvironment() === ENV_NAME.DEV) return true;
  return flag;
};

/**
 * Gates the in-session "Test microphone" entry points (the mic-test modal
 * opened from the mic dropdown) on both the V1 and V2 session layouts.
 * Web only — the modal reads levels from the web audio graph, which the
 * Tauri desktop recorder doesn't use, so desktop surfaces never render the
 * entry regardless of this flag. Rollout/kill gate, not an experiment — no
 * useManualExposure.
 */
export const useIsMicTestEnabled = () => {
  const flagEnabled = useBooleanFlagValue('release-scribe-enable-mic-test', false);
  if (isTauri()) return false;
  return flagEnabled;
};

export { useIsTeamsTableRedesignEnabled } from '@heidi/workspace/flags';

/**
 * Gates the form editor (Field Recognition) bulk field actions as
 * one unit: the Detected Fields header "⋯" menu (form-wide font size + field
 * type with confirmation dialogs), the per-field "Apply to all fields" offer
 * under the font size editor, and the sidebar layout rearrangement (add-field
 * button relocated to the page row, replacing the prev/next field chevrons).
 * When OFF (default), the legacy sidebar renders exactly as before — header
 * add-field button, chevron field navigation, no bulk actions. Rollout/kill
 * gate, NOT an experiment — do not add useManualExposure.
 */
export const useIsFormEditorBulkActionsEnabled = () => {
  return useFeatureFlagEnabled('release-growth-form-editor-bulk-actions') ?? false;
};

/**
 * Gates the Settings > Defaults "Default form field font size" control and
 * applying a saved default to newly detected / added fields. When the user has
 * not saved a preference, behaviour stays PDF Default (`null`) — no override.
 * When OFF, upload extraction leaves unset font sizes unchanged; manual add
 * keeps the legacy Auto (`0`) behaviour matching main.
 * Rollout/kill gate — not an experiment; no useManualExposure.
 */
export const useIsDefaultFormFieldFontSizeEnabled = () => {
  return useFeatureFlagEnabled('release-growth-default-form-field-font-size') ?? false;
};

export const AMBIENT_COPILOT_FLAG_KEY = 'release-scribe-enable-ambient-copilot';

/**
 * UMBRELLA gate for the ENTIRE ambient Heidi Copilot surface: the
 * `copilot-overlay` Tauri window (visibility + panel styling), the
 * `CopilotIntentHost` / `CopilotActiveNotePublisher` / `CopilotPrechartOrchestrator`
 * main-window hosts, and the Copilot-origin pre-chart handoff. New Copilot UI or
 * IPC surface? Wrap it in this.
 *
 * Default OFF. Desktop-only — Copilot has no web surface, so this is `false` on
 * web regardless of the flag. Rollout/kill gate, NOT an experiment — do not add
 * useManualExposure.
 *
 * NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS only patches PostHog, not
 * OpenFeature, so honour it explicitly here (mirrors
 * `useIsOfflineRecordingEnabled`) — otherwise local devs on force-all can never
 * see Copilot.
 */
export const useIsAmbientCopilotEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(AMBIENT_COPILOT_FLAG_KEY, false);
  if (!isTauri()) return false;
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

export const AX_NAVIGATION_FLAG_KEY = 'release-rpa-copilot-enable-ax-navigation';

/**
 * Kill/rollout gate for Copilot EHR warm AX navigation (`navigateToCheckpoint`).
 * Default OFF. Desktop-only — no web surface. Rollout/kill gate, NOT an
 * experiment — do not add useManualExposure.
 *
 * The gate is enforced inside the nav module (not per-caller). Sync accessor
 * drives `navigateToCheckpoint`; the React hook is for future hosts/tests.
 * Both share `gateAxNavigation` so they cannot disagree.
 *
 * NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS only patches PostHog, not
 * OpenFeature, so honour it explicitly here (mirrors ambient-copilot /
 * offline-recording).
 */
const gateAxNavigation = (readFlag: () => boolean): boolean => {
  if (!isTauri()) return false;
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return readFlag();
};

export const isAxNavigationEnabled = (): boolean =>
  gateAxNavigation(() =>
    OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
      AX_NAVIGATION_FLAG_KEY,
      false
    )
  );

export const useIsAxNavigationEnabled = (): boolean => {
  const fromFlagsmith = useBooleanFlagValue(AX_NAVIGATION_FLAG_KEY, false);
  return gateAxNavigation(() => fromFlagsmith);
};

/**
 * Gates previewing a Context tab attachment: on desktop, clicking a file chip
 * opens the original upload instead of doing nothing. Kill switch for a failing
 * blob fetch or a renderer crash on an unexpected format.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out.
 */
export { useIsContextDocumentPreviewEnabled } from '@heidi/session/flags';

/**
 * SCR-6774: gates `.txt` / `.md` in the Context tab uploader — the file picker,
 * drag/drop, paste, and the "Supported file types" copy that advertises them.
 *
 * FE-only rollout and kill switch. ml-scribe accepts text unconditionally once
 * backend PR #16228 is deployed, so keep this OFF until that deployment is
 * complete; enabling it earlier would offer formats the backend rejects.
 * Default OFF.
 */
export { useIsTextContextDocumentUploadEnabled } from '@heidi/session/flags';

/**
 * Rollout gate and kill switch for failed context-file chips: ON keeps a chip
 * whose upload or extraction failed visible and deletable. Default OFF.
 */
export { useIsContextUploadFailedChipsEnabled } from '@heidi/session/flags';

/**
 * Rollout gate and kill switch for the post-recording poor-audio warning
 * banner. Default OFF.
 */
export { useIsPoorAudioWarningEnabled } from '@heidi/session/flags';

/**
 * SCR-6633: optimized audio bar visualizers — compositor-backed transform
 * "caps" plus 30 fps rAF throttling replacing the per-frame `height`
 * animation across the scribe / dictation / chronicle level meters. The
 * switch lives inside each bar family (`mic-selector`, `audio-bar-meter`,
 * `live-audio-visualizer`, dictation widget bars): OFF renders the `legacy/`
 * copies, byte-identical to pre-SCR-6633 main. Rollout/kill gate, NOT an
 * experiment — do not add useManualExposure. Default OFF; flip
 * per-environment in Flagsmith.
 */
export const useIsOptimizedAudioBarsEnabled = () => {
  return useBooleanFlagValue('release-scribe-optimized-audio-bars', false);
};

/**
 * Umbrella gate for the "Start now for current sessions" experience: stacked
 * sub-labels, progressive date display, gutter checkboxes, dedicated actions
 * area, Upcoming in split view, and individuals vs teams views.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out.
 */
export { useIsStartNowEnabled } from '@heidi/session/flags';

/**
 * Flagsmith: workspace-selection step in the widget auth flow. When OFF, the
 * flow stays sign-in → link → upgrade-plan (3 steps) and the linked account
 * keeps the default workspace.
 */
export const useIsWidgetAuthWorkspaceSelectEnabled = () => {
  return useBooleanFlagValue('release-select-workspace-in-widget-auth', false);
};

/**
 * PostHog kill switch: the `/scribe` boot after accepting a team invite shows
 * a bare loader and holds for the Heidi II welcome. Read at invite accept, never
 * on the boot itself, where flags have not resolved. Default OFF.
 */
export const useIsTeamJoinBootLoaderEnabled = () => {
  return useFeatureFlagEnabled('release-team-join-boot-loader') ?? false;
};

/**
 * Independent rollout for the prominent session-list primary action and its
 * supporting toolbar/header layout.
 */
export {
  isNoteGenerationRecoveryEnabled,
  useIsNoteGenerationRecoveryEnabled,
  useIsSessionListPrimaryActionEnabled,
} from '@heidi/session/flags';

/** A FAILED note with a saved user edit shows the edit instead of the error overlay. */
export { useIsFailedNoteRescueEnabled } from '@heidi/session/flags';

/**
 * UMBRELLA gate for the collapsed-Upcoming session-list experience. The single
 * derivation point (`collapsedUpcomingMode` in `use-session-list-query-state`)
 * ANDs this flag with the default date-DESC sort and every surface derives from
 * that one value, so OFF is byte-identical pre-feature behaviour with no half-on
 * state. Rollout/kill gate, NOT an experiment — do not add useManualExposure.
 *
 * MOVED to `@heidi/session` by ruling 36's whole-class fan-out; its
 * `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` branch COLLAPSED onto the door's
 * injected `forceEnableAll`.
 */
export { useIsCollapsedUpcomingSessionsEnabled } from '@heidi/session/flags';
